/**
 * WorldUI: in-scene hub UI for signed-in users.
 *
 * Adds a settings button to the top-left (#top-menu). Opening it shows the
 * main menu panel (user card + entry grid, styled after docs/ui/首页.jpg) and
 * its sub-panels: 世界场景 / 创建场景 / 加入场景 / 角色 / 分享链接 / 公告 /
 * 帮助 / 反馈. All data flows through the authenticated /api/v1/hub endpoints,
 * so the UI and the room management backend stay in sync (管理打通).
 *
 * Room switching hands control back to the join flow via onJoinRoom, which
 * main.ts maps to location.assign('/join?roomId=…&sceneId=…&invite=…').
 */

import type { UiManager } from './UiManager';
import { toVirtualLandscapePoint } from '../input/input-controller';
import { isMobileRuntime } from '../app/createApp';
import { ensurePlayableGlb } from '../network/glbMeshopt';
import { AVATAR_SIZE_SCALE } from '../config/avatar.config';
import { levelTitle } from '../../../../vrroom_typescript/src/platform/contracts';
import type { MusicSyncState } from '../../../../vrroom_typescript/src/platform/contracts';

type Api = <T = unknown>(path: string, method?: string, body?: unknown) => Promise<T>;

export interface WorldUIOptions {
    user: { id: number; username: string; level: number; role?: string; avatarId?: string };
    api: Api;
    /** The room this session was admitted into (used for share + "当前房间"). */
    currentRoom: { roomId: string; sceneId: string; invite: string };
    onJoinRoom: (target: { roomId: string; sceneId: string; invite: string }) => void;
    /** 世界场景（离线单人漫游）：按场景 id 直接进入，不进入联机房间。 */
    onEnterScene: (sceneId: string) => void;
    /** 角色面板切换形象成功后通知主程序：本地第三人称模型立即重建。 */
    onAvatarChange?: (avatarId: string) => void;
    /** 角色模型页签预览某形象时通知主程序（url 为该形象的 modelUrl）：
     *  主应用借此提前下载/解析 GLB 预热资产缓存——预览舞台是独立小应用，
     *  它加载的资源主场景用不了，不预热的话点「使用」还要重新解析数秒。
     *  null = 离开页签/关闭面板，释放预热的引用。 */
    onPreviewAvatar?: (url: string | null) => void;
    /** 主持人「物体」页签：放置模型（socket 命令 room:object-spawn，由
     *  main.ts 进入光标放置会话并广播全员）；移动（Gizmo 编辑会话）与
     *  删除已放置物体。 */
    onPlaceObject?: (model: { id: string; name: string; modelUrl: string }) => void;
    onEditObject?: (id: string) => void;
    onRemoveObject?: (id: string) => Promise<void>;
    /** 主持人「音乐」页签：背景音乐控制（socket 命令 room:music-control，由
     *  main.ts 注入 session.musicControl，服务端权威广播含本人回环应用）；
     *  getMusicState 供面板轮询当前同步态（play 按钮/滑杆随事件刷新）。 */
    onMusicControl?: (action: 'play' | 'resume' | 'pause' | 'stop' | 'volume', trackId?: string, value?: number) => Promise<void>;
    getMusicState?: () => MusicSyncState | null;
    /** 主持人「成员管理」页签：头牌（名牌）一键隐藏/显示（socket 命令
     *  room:nameplates-control，服务端权威广播含本人）；getNameplatesHidden
     *  供面板轮询当前开关态（按钮文案随之切换）。 */
    onNameplatesControl?: (hidden: boolean) => Promise<void>;
    getNameplatesHidden?: () => boolean;
    /** 成员列表本地合成数据（快照 + 30Hz players:state 本地维护，含本人）：
     *  面板零延迟渲染、mic/禁言/进出房实时翻转，HTTP /members 降为后台
     *  校准（防重连等场景漏事件漂移）。由 main.ts 注入。 */
    getLocalMembers?: () => MemberRow[];
    /** 主持人权限变更通知（初始查询 + player:moderator 动态授权/收回）。
     *  granted = 房主 / 口令授权主持人 / 口令授权副主持人 / 平台管理员任一身份。 */
    onModeratorStateChange?: (granted: boolean) => void;
}

interface RoomRow {
    id: string; name: string; sceneId: string; sceneName: string;
    maxParticipants: number; hasPassword: boolean; occupiedCount: number; isHost: boolean;
    hostName?: string; createdAt?: string;
}

/** 内置模型库条目（GET /hub/models；assets/models 目录扫描注册）。 */
interface ModelRow { id: string; name: string; modelUrl: string; thumbUrl?: string; }

/** 背景音乐曲目条目（GET /hub/tracks；assets/audio 目录扫描注册）。 */
interface TrackRow { id: string; name: string; trackUrl: string; }

interface AvatarRow {
    id: string; name: string; modelUrl: string;
    config: { gender?: string; idleAnim?: string; avatarScale?: number; avatarRotationOffset?: number; groundOffset?: number; [k: string]: unknown };
}

interface AnnounceRow {
    id: number; content: string; authorName?: string; createdAt?: string;
}

interface FeedbackRow {
    id: number; username: string; content: string; createdAt?: string;
}

interface FeedbackPage {
    items: FeedbackRow[]; total: number; page: number; pageSize: number;
}

/** 本会话在当前房间的管理身份（GET /hub/rooms/:id/moderation）。
 *  isModerator=口令主持人，isCoHost=口令副主持人（两席位各自唯一）。 */
interface ModerationState { isHost: boolean; isModerator: boolean; isCoHost: boolean; isAdmin: boolean; }

/** 房间在线成员（GET /admin/rooms/:id/members）。mic = 当前是否开麦。 */
interface MemberRow {
    userId: number; username: string; organization: string; level: number;
    status: string; muted?: boolean; mic?: boolean;
}

/** 房间禁入名单（GET /admin/rooms/:id/bans，字段保持服务端蛇形命名）。 */
interface BanRow { user_id: number; username: string; reason: string | null; }

type Gender = 'all' | 'male' | 'female';

const MENU_ENTRIES: { action: string; icon: string; label: string }[] = [
    { action: 'world', icon: '🌍', label: '世界场景' },
    { action: 'create', icon: '🏗️', label: '创建场景' },
    { action: 'join', icon: '🚪', label: '加入场景' },
    { action: 'avatar', icon: '🧑', label: '角色' },
    { action: 'share', icon: '🔗', label: '分享链接' },
    { action: 'announce', icon: '📢', label: '公告' },
    { action: 'help', icon: '❓', label: '帮助' },
    { action: 'feedback', icon: '💬', label: '反馈' }
];

function h<K extends keyof HTMLElementTagNameMap>(tag: K, className = '', text = ''): HTMLElementTagNameMap[K] {
    const node = document.createElement(tag);
    node.className = className;
    if (text) node.textContent = text;
    return node;
}

async function copyText(text: string): Promise<void> {
    // clipboard API 只在安全上下文存在，且在页面失焦/权限不足时会 reject；
    // 两种失败都要退回 execCommand，否则复制按钮会静默失效。
    try {
        if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return; }
    } catch { /* fall through to execCommand */ }
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.left = '-9999px';
    area.style.top = '0';
    document.body.appendChild(area);
    area.focus();
    area.select();
    try { if (!document.execCommand('copy')) throw new Error('复制失败'); }
    finally { area.remove(); }
}

const errorMessage = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** 场景缩略图：优先加载 /assets/scenes/<id>/thumb.png，失败回退首字母渐变块。 */
function sceneThumb(sceneId: string, name: string): HTMLElement {
    const thumb = document.createElement('div');
    thumb.className = 'ws-thumb';
    thumb.classList.add(`ws-thumb-${Math.abs(hashCode(sceneId)) % 5}`);
    const fallback = document.createElement('span');
    fallback.textContent = name.slice(0, 1);
    const img = document.createElement('img');
    img.alt = '';
    img.decoding = 'async';
    img.addEventListener('error', () => img.remove());
    img.addEventListener('load', () => fallback.remove());
    img.src = `/assets/scenes/${encodeURIComponent(sceneId)}/thumb.png`;
    thumb.append(fallback, img);
    return thumb;
}

function hashCode(text: string): number {
    let v = 0;
    for (let i = 0; i < text.length; i++) v = (v * 31 + text.charCodeAt(i)) | 0;
    return v;
}

/** 房间卡片创建时间：MM-DD HH:mm（卡片内空间有限，省略年份）。 */
function formatRoomTime(iso?: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const p = (n: number) => String(n).padStart(2, '0');
    return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** 反馈/公告完整时间：YYYY-MM-DD HH:mm。 */
function formatDateTime(iso?: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** 卡片轮播（创建场景「新建/我的」两页签共用同一布局）：固定 3 列，
 *  每页排数按容器高度自适应——高度只够一排时每页 3 张，其余翻页查看。 */
function carouselOf(cards: HTMLElement[]): { root: HTMLElement } {
    const COLS = 3; // 与 .wc-grid 固定 3 列轨道一致
    const ROW_GAP = 14; // .wc-grid 行间距
    let perPage = 6; // 挂载前先按 2 排渲染，挂载后按实际高度自适应
    let page = 0;
    const pageCount = () => Math.max(1, Math.ceil(cards.length / perPage));
    const grid = h('div', 'wc-grid');
    const prev = h('button', 'wc-page-btn wc-page-prev', '‹');
    const next = h('button', 'wc-page-btn wc-page-next', '›');
    const indicator = h('span', 'wc-page-ind');
    const renderPage = () => {
        grid.innerHTML = '';
        for (const card of cards.slice(page * perPage, page * perPage + perPage)) grid.append(card);
        indicator.textContent = `${page + 1} / ${pageCount()}`;
    };
    prev.addEventListener('click', () => { page = (page - 1 + pageCount()) % pageCount(); renderPage(); });
    next.addEventListener('click', () => { page = (page + 1) % pageCount(); renderPage(); });
    renderPage();
    const root = h('div', 'wc-carousel');
    root.append(grid, prev, next, indicator);
    // 容器尺寸变化（挂载、页签 hidden→显示、窗口缩放）时重算每页排数。
    new ResizeObserver(() => {
        const cardH = grid.querySelector<HTMLElement>('.wc-card')?.offsetHeight ?? 0;
        if (!cardH) return; // 不可见时保持现值，待显示后再校准
        const rows = Math.max(1, Math.floor((root.clientHeight + ROW_GAP) / (cardH + ROW_GAP)));
        const nextPerPage = rows * COLS;
        if (nextPerPage !== perPage) {
            perPage = nextPerPage;
            page = Math.min(page, pageCount() - 1);
            renderPage();
        }
    }).observe(root);
    return { root };
}

class WorldUI {
    private ui: UiManager;
    private opts: WorldUIOptions;
    private menu: HTMLElement;
    private menuBtn: HTMLElement;
    private hostBtn: HTMLElement;
    /** 本会话是否可管理当前房间（房主 / 主持人 / 平台管理员任一）。 */
    private moderator = false;
    /** 各管理身份的分项旗标（口令授权席位可变：抢权/被顶替/自我取消）。 */
    private modState: ModerationState = { isHost: false, isModerator: false, isCoHost: false, isAdmin: false };
    private fullscreen: HTMLElement | null = null;
    private fullscreenCleanups: (() => void)[] = [];
    private modal: HTMLElement | null = null;
    /** 面板列表会话级缓存（stale-while-revalidate）：模型/曲目/禁入几乎
     *  不变，切页签先秒出缓存再后台刷新；面板关闭不清空（跨打开复用）。 */
    private modelCache: ModelRow[] | null = null;
    private trackCache: TrackRow[] | null = null;
    private banCache: BanRow[] | null = null;

    constructor(ui: UiManager, opts: WorldUIOptions) {
        this.ui = ui;
        this.opts = opts;
        this.menuBtn = this._buildSettingsButton();
        this.hostBtn = this._buildHostButton();
        this.menu = this._buildMenu();
        void this._syncModeratorState();
        window.addEventListener('keydown', (e) => {
            if (e.key !== 'Escape') return;
            if (this.modal) { this.closeModal(); return; }
            if (this.fullscreen) { this.closeFullscreen(); return; }
            if (!this.menu.classList.contains('hidden')) this.closeMenu();
        });
    }

    /** 当前是否持有本房间管理身份（房主/口令主持人/平台管理员任一）。
     *  F2 编辑器等主持人专属能力的只读查询入口。 */
    get isModerator(): boolean { return this.moderator; }

    /** 房间变更（传送跳场景 onLoad）后重拉管理身份：口令授权不跨房——
     *  服务端已随跳转删除旧房授权，本地 moderator 状态必须随新房间重置，
     *  「主持人」按钮回到口令授权态，输新场景自己的口令重新获取。 */
    refreshModeration(): void { void this._syncModeratorState(); }

    // ── Settings button + main menu ────────────────────────────────────────

    private _buildSettingsButton(): HTMLElement {
        // 初始隐藏：场景就绪后由 revealSettingsButton() 显示。
        const btn = h('button', 'hidden');
        btn.id = 'world-settings-btn';
        btn.ariaLabel = '设置';
        btn.title = '设置';
        // 齿轮图标（lucide settings path），随按钮 currentColor 取墨玉鎏金色
        btn.innerHTML = '<svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
            + '<path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>'
            + '<circle cx="12" cy="12" r="3"/></svg>';
        btn.addEventListener('click', () => this.toggleMenu());
        const menu = document.getElementById('top-menu');
        (menu ?? document.body).prepend(btn);
        return btn;
    }

    /** 场景就绪后显示 ⚙ 设置按钮与 🛡 主持人按钮（登录/场景加载阶段保持隐藏）。 */
    revealSettingsButton(): void {
        this.menuBtn.classList.remove('hidden');
        this.hostBtn.classList.remove('hidden');
    }

    private _buildMenu(): HTMLElement {
        const panel = h('div', 'hidden');
        panel.id = 'world-menu';
        const u = this.opts.user;
        const head = h('div', 'wm-head');
        const avatar = h('div', 'wm-avatar', u.username.slice(0, 1).toUpperCase());
        const id = h('div', 'wm-id');
        id.append(h('strong', '', u.username), h('span', '', `ID ${u.id} · ${levelTitle(u.level)} ${u.level}级`));
        // 退出：菜单头部右上角图标（淡红描边保留「离开类操作」语义）。
        // 点击直接关闭网页（手机端/电脑端通用）：window.close 对脚本打开的
        // 窗口与多数手机内置浏览器有效；PC 浏览器通常拒绝关闭用户手动打开的
        // 标签页，先用 _self 重开抹掉该限制再关，仍被拒绝则退回空白页兜底，
        // 三段式保证两端都能立即中断场景与连接。
        const logout = h('button', 'wm-logout');
        logout.title = '关闭网页';
        logout.ariaLabel = '关闭网页';
        // 登出图标（lucide log-out path），随按钮 currentColor 取色。
        logout.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
            + '<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" x2="9" y1="12" y2="12"/></svg>';
        logout.addEventListener('click', () => {
            // 显式退出 ≠ 刷新：清掉轻量重连标记（sessionStorage 按 TAB 存活，
            // about:blank 兜底跳转后标记仍在，重进会走 room:resume 恢复退出前
            // 的位置与朝向）。清掉后重进 = room:join 重新出生（区内随机位置 +
            // 出生点统一朝向）。F5/断线的恢复语义不受影响（pagehide 不清）。
            try { sessionStorage.removeItem(`room-resume:${u.id}`); } catch { /* 隐私模式等 */ }
            try {
                window.opener = null;
                window.open('', '_self');
            } catch { /* 忽略，走下方 close/兜底 */ }
            window.close();
            // 关闭被浏览器拒绝时的兜底：跳转空白页，同样立即终止页面。
            window.setTimeout(() => { location.href = 'about:blank'; }, 200);
        });
        head.append(avatar, id, logout);
        const tagline = h('div', 'wm-tagline', '虚数灵境元宇宙');
        const grid = h('div', 'wm-grid');
        for (const entry of MENU_ENTRIES) {
            // 创建场景仅平台管理员可见：开房入口收敛到管理员。加入场景对
            // 全部成员开放（与「世界场景」同源 /hub/rooms，服务端本就放行
            // 所有登录用户浏览开放房间并进入）。
            if (entry.action === 'create' && this.opts.user.role !== 'admin') continue;
            const btn = h('button', 'wm-item');
            btn.append(h('span', 'wm-icon', entry.icon), h('span', 'wm-label', entry.label));
            btn.addEventListener('click', () => { this.closeMenu(); this._dispatch(entry.action); });
            grid.append(btn);
        }
        panel.append(head, tagline, grid);
        panel.addEventListener('click', (e) => { if (e.target === panel) this.closeMenu(); });
        const layer = document.getElementById('ui-layer') ?? document.body;
        layer.appendChild(panel);
        return panel;
    }

    private _dispatch(action: string): void {
        switch (action) {
            case 'world': void this.openWorld(); break;
            case 'create': void this.openCreate(); break;
            case 'join': void this.openJoin(); break;
            case 'avatar': void this.openAvatar(); break;
            case 'share': void this.openShare(); break;
            case 'announce': this.openAnnounce(); break;
            case 'help': this.ui.toggleHelp(true); break;
            case 'feedback': this.openFeedback(); break;
        }
    }

    toggleMenu(): void {
        const opening = this.menu.classList.contains('hidden');
        if (opening) {
            this.closeFullscreen();
            this.closeModal();
            this.menu.classList.remove('hidden');
            this.ui.requestUnlock?.();
            this.ui.onPopupOpen?.();
        } else {
            this.closeMenu();
        }
    }

    closeMenu(): void {
        this.menu.classList.add('hidden');
        this.ui.onPopupClose?.();
    }

    /** 保存资料后同步主菜单里的头像字与用户名。 */
    private refreshMenuIdentity(): void {
        const u = this.opts.user;
        const strong = this.menu.querySelector('.wm-id strong');
        if (strong instanceof HTMLElement) strong.textContent = u.username;
        const avatar = this.menu.querySelector('.wm-avatar');
        if (avatar instanceof HTMLElement) avatar.textContent = u.username.slice(0, 1).toUpperCase();
    }

    // ── 主持人（口令授权 + 房间管理）────────────────────────────────────────

    /** 查询本会话在当前房间的管理身份（房主/主持人/副主持人/管理员），
     *  决定 🛡 按钮是直接打开控制台还是先弹口令输入框。 */
    private async _syncModeratorState(): Promise<void> {
        if (this.opts.user.role === 'admin') {
            this.modState = { isHost: false, isModerator: false, isCoHost: false, isAdmin: true };
            this.moderator = true;
            this._syncHostButton();
            this.opts.onModeratorStateChange?.(true);
            return;
        }
        const roomId = this.opts.currentRoom.roomId;
        if (!roomId) return; // solo 离线漫游无房间
        try {
            const s = await this.opts.api<ModerationState>(
                `/hub/rooms/${encodeURIComponent(roomId)}/moderation`);
            this.modState = s;
            this.moderator = s.isHost || s.isModerator || s.isCoHost || s.isAdmin;
            this._syncHostButton();
            this.opts.onModeratorStateChange?.(this.moderator);
        } catch { /* 查询失败不阻塞：点击时仍走授权弹窗 */ }
    }

    /** 口令授权变更（player:moderator，本人）：上位/被顶替/自我取消。
     *  coHost=本次授权的席位是否为副主持人。被顶替且无其他管理身份时
     *  收起开着的控制台并恢复「授权」按钮态。 */
    onModerationChanged(granted: boolean, coHost = false): void {
        this.modState.isModerator = granted && !coHost;
        this.modState.isCoHost = granted && coHost;
        const had = this.moderator;
        this.moderator = this.modState.isHost || this.modState.isModerator || this.modState.isCoHost || this.modState.isAdmin;
        this._syncHostButton();
        this.opts.onModeratorStateChange?.(this.moderator);
        if (granted) {
            this.ui.showHint(coHost ? '你已成为本房间副主持人' : '你已成为本房间主持人');
        } else if (had && !this.moderator) {
            this.closeFullscreen();
            this.ui.showHint('主持人权限已被接管或取消', 5000);
        }
    }

    private _syncHostButton(): void {
        this.hostBtn.classList.toggle('granted', this.moderator);
        this.hostBtn.title = this.moderator ? '管理控制台' : '管理员授权';
        this.hostBtn.ariaLabel = this.hostBtn.title;
    }

    /** 🛡 管理员按钮：紧跟 ⚙ 齿轮按钮。未授权点击弹口令输入框（主持/
     *  副主持口令分别对应两席位）；已是管理者（房主/主持人/副主持人/
     *  管理员）点击直接打开控制台。 */
    private _buildHostButton(): HTMLElement {
        const btn = h('button', 'hidden');
        btn.id = 'host-console-btn';
        btn.title = '管理员授权';
        // 盾牌图标（lucide shield path），与齿轮按钮同款圆形玻璃底
        btn.innerHTML = '<svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
            + '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/></svg>';
        btn.addEventListener('click', () => {
            if (this.moderator) this.openHostConsole();
            else void this._openGrantModal();
        });
        this.menuBtn.after(btn);
        return btn;
    }

    /** 口令授权弹窗：输入口令 → POST /hub/rooms/:id/host-grant。
     *  命中主持人口令得主持人席位、命中副主持人口令得副主持人席位
     *  （两席位各自唯一，后输入者顶替同席前任），通过后直接进入控制台。 */
    private _openGrantModal(): Promise<void> {
        const roomId = this.opts.currentRoom.roomId;
        if (!roomId) { this.ui.showHint('离线漫游暂不支持主持人功能', 5000); return Promise.resolve(); }
        const body = this._openModal('管理员授权');
        const field = h('label', 'wc-field');
        field.append(h('span', '', '管理口令'));
        const input = h('input');
        input.type = 'password';
        input.maxLength = 64;
        input.autocomplete = 'off';
        input.placeholder = '请输入口令';
        field.append(input);
        const msg = h('p', 'wc-msg');
        const actions = h('div', 'wshare-actions');
        const submit = h('button', 'wshare-btn', '确认授权');
        submit.addEventListener('click', async () => {
            const code = input.value.trim();
            if (!code) { msg.textContent = '请输入口令。'; return; }
            submit.disabled = true;
            msg.textContent = '正在验证…';
            try {
                const r = await this.opts.api<{ moderator: boolean; coHost?: boolean }>(
                    `/hub/rooms/${encodeURIComponent(roomId)}/host-grant`,
                    'POST', { code });
                this.modState.isModerator = !r.coHost;
                this.modState.isCoHost = r.coHost === true;
                this.moderator = true;
                this._syncHostButton();
                this.closeModal();
                this.ui.showHint(r.coHost ? '已获得副主持人权限' : '已获得主持人权限');
                this.openHostConsole();
            } catch (err) {
                const code2 = (err as { code?: string }).code;
                msg.textContent = code2 === 'CODE_INVALID' ? '口令不正确。'
                    : code2 === 'NOT_IN_ROOM' ? '请先进入该房间后再授权。'
                    : code2 === 'TOO_MANY_ATTEMPTS' ? '尝试过于频繁，请稍后再试。'
                    : errorMessage(err);
                submit.disabled = false;
            }
        });
        actions.append(submit);
        const note = h('p', 'wm-modal-note',
            '输入主持人口令获得主持人权限；输入副主持人口令获得副主持人权限。');
        body.append(field, note, actions, msg);
        input.focus();
        return Promise.resolve();
    }

    /** 主持人控制台：成员管理（查看/踢出并禁入）+ 禁入名单（解除禁入）
     *  + 口令管理（仅平台管理员）。数据全部走既有 /admin/* 端点，
     *  成员列表 5s 轮询，面板关闭时经 register 清理定时器。 */
    private openHostConsole(): void {
        const roomId = this.opts.currentRoom.roomId;
        if (!roomId) { this.ui.showHint('离线漫游暂不支持主持人功能', 5000); return; }
        const base = `/admin/rooms/${encodeURIComponent(roomId)}`;
        this._openFullscreen('主持人控制', '🛡️', (body, register) => {
            const tabs = h('div', 'wf-tabs');
            const memberPane = h('div', 'wc-pane wh-pane');
            const banPane = h('div', 'wc-pane wh-pane hidden');
            const objectPane = h('div', 'wc-pane wh-pane hidden');
            const musicPane = h('div', 'wc-pane wh-pane hidden');
            // 口令管理页签仅平台管理员可见（后端同样只放行 admin）。
            // 「物体」页签（添加/管理 GLB 模型）仅 PC 端可用：放置与 Gizmo
            // 编辑会话依赖鼠标（光标瞄准/滚轮调距/右键转视角/W·E 切模式）。
            const isAdmin = this.opts.user.role === 'admin';
            const codePane = h('div', 'wc-pane wh-pane hidden');
            const tabBtns: Record<'members' | 'bans' | 'objects' | 'music' | 'code', HTMLButtonElement> = {
                members: h('button', 'wf-tab active', '成员管理'),
                bans: h('button', 'wf-tab', '禁入名单'),
                objects: isMobileRuntime() ? (null as unknown as HTMLButtonElement) : h('button', 'wf-tab', '物体'),
                music: h('button', 'wf-tab', '音乐'),
                code: isAdmin ? h('button', 'wf-tab', '口令管理') : (null as unknown as HTMLButtonElement),
            };
            const panes = { members: memberPane, bans: banPane, objects: objectPane, music: musicPane, code: codePane };
            const syncTabs = (active: 'members' | 'bans' | 'objects' | 'music' | 'code') => {
                for (const key of ['members', 'bans', 'objects', 'music', 'code'] as const) {
                    tabBtns[key]?.classList.toggle('active', key === active);
                    panes[key].classList.toggle('hidden', key !== active);
                }
                if (active === 'bans') void loadBans();
                if (active === 'objects') void loadObjectsTab();
                if (active === 'music') void loadMusicTab();
                if (active === 'code') void loadCode();
            };
            for (const key of ['members', 'bans', 'objects', 'music', 'code'] as const)
                tabBtns[key]?.addEventListener('click', () => syncTabs(key));
            tabs.append(...Object.values(tabBtns).filter(Boolean) as HTMLElement[]);
            body.append(tabs, memberPane, banPane, objectPane, musicPane, codePane);

            // ── 成员管理：在线成员 + 踢出（踢出并禁入，可在禁入名单解除）──
            // 顶部操作行：一键禁言/取消禁言全场（同一按钮随状态切换文案）居左，
            // 「取消我的主持人/副主持人权限」居右（仅口令授权席位显示；房主/平台
            // 管理员的身份不受影响、无此按钮）。
            const hostActions = h('div', 'wh-host-actions');
            const muteAll = h('button', 'wh-mute-all', '一键禁言全场');
            // 是否有成员处于禁言：决定按钮文案（禁言全场 ↔ 取消全场禁言）。
            let anyMuted = false;
            const syncMuteAll = () => {
                muteAll.textContent = anyMuted ? '取消全场禁言' : '一键禁言全场';
                muteAll.classList.toggle('unmuted', anyMuted);
            };
            muteAll.addEventListener('click', async () => {
                if (anyMuted) {
                    const ok = await this.ui.showConfirm(
                        '确定取消全场禁言？将解除本房间所有成员（含离线成员）的禁言。',
                        '全部解除', '再想想');
                    if (!ok) return;
                    try {
                        const res = await this.opts.api<{ muted: number }>(`${base}/mutes`, 'POST', { all: true, muted: false });
                        this.ui.showHint(`已解除全场禁言 ${res.muted} 人`);
                        refreshMicSoon();
                    } catch (err) {
                        this.ui.showHint(`操作失败：${errorMessage(err)}`, 5000);
                    }
                    return;
                }
                const ok = await this.ui.showConfirm(
                    '确定禁言全场？除管理员/房主/主持人外的所有在线成员将被禁言。',
                    '全部禁言', '再想想');
                if (!ok) return;
                try {
                    const res = await this.opts.api<{ muted: number }>(`${base}/mutes`, 'POST', { all: true, muted: true });
                    this.ui.showHint(`已禁言全场 ${res.muted} 人`);
                    refreshMicSoon();
                } catch (err) {
                    this.ui.showHint(`操作失败：${errorMessage(err)}`, 5000);
                }
            });
            // 头牌（名牌）一键隐藏/显示（同一按钮随开关态切换文案）：与禁言
            // 全场并列居左（包一层左组，space-between 布局下「取消权限」仍居右）。
            // socket 命令 room:nameplates-control，服务端权威广播含本人回环应用；
            // 文案 1s 轮询开关态（与音乐面板同款节奏），其他主持人切换时按钮也
            // 能跟上。复用 .wh-mute-all 样式与 unmuted 变体。
            const leftActions = h('div');
            leftActions.style.cssText = 'display:flex;align-items:center;gap:10px;flex:none';
            const plateToggle = h('button', 'wh-mute-all', '一键隐藏头牌');
            const syncPlateToggle = () => {
                const hidden = this.opts.getNameplatesHidden?.() === true;
                plateToggle.textContent = hidden ? '一键显示头牌' : '一键隐藏头牌';
                plateToggle.classList.toggle('unmuted', hidden);
            };
            plateToggle.addEventListener('click', () => {
                void this.opts.onNameplatesControl?.(this.opts.getNameplatesHidden?.() !== true);
            });
            leftActions.append(muteAll, plateToggle);
            hostActions.append(leftActions);
            const plateTimer = window.setInterval(syncPlateToggle, 1000);
            register(() => window.clearInterval(plateTimer));
            syncPlateToggle();
            if (this.modState.isModerator || this.modState.isCoHost) {
                const cohostOnly = this.modState.isCoHost && !this.modState.isModerator;
                const resign = h('button', 'wh-resign',
                    cohostOnly ? '取消我的副主持人权限' : '取消我的主持人权限');
                resign.addEventListener('click', async () => {
                    const ok = await this.ui.showConfirm(
                        cohostOnly
                            ? '确定取消本房间的副主持人权限？取消后需重新输入口令才能获得。'
                            : '确定取消本房间的主持人权限？取消后需重新输入口令才能获得。',
                        '确定取消', '再想想');
                    if (!ok) return;
                    try {
                        await this.opts.api(
                            `/hub/rooms/${encodeURIComponent(roomId)}/host-grant`, 'DELETE');
                        this.modState.isModerator = false;
                        this.modState.isCoHost = false;
                        this.moderator = this.modState.isHost || this.modState.isAdmin;
                        this._syncHostButton();
                        this.closeFullscreen();
                        this.ui.showHint(cohostOnly ? '已取消副主持人权限' : '已取消主持人权限');
                    } catch (err) {
                        this.ui.showHint(`操作失败：${errorMessage(err)}`, 5000);
                    }
                });
                hostActions.append(resign);
            }
            memberPane.append(hostActions);
            const memberList = h('div', 'wh-list');
            memberPane.append(memberList);
            const statusText = (s: string) =>
                s === 'online' ? '在线' : s === 'loading' ? '加载中'
                : s === 'transferring' ? '传送中' : s === 'reconnecting' ? '重连中' : s;
            const kick = async (row: MemberRow): Promise<void> => {
                const ok = await this.ui.showConfirm(
                    `确定将「${row.username}」移出房间并禁止再次进入？禁入在全员退出房间后自动解除。`, '踢出', '取消');
                if (!ok) return;
                try {
                    await this.opts.api(`${base}/kicks`, 'POST',
                        { userId: row.userId, reason: '主持人移出' });
                    this.ui.showHint(`已将 ${row.username} 移出房间`);
                    refreshMicSoon();
                } catch (err) {
                    this.ui.showHint(`操作失败：${errorMessage(err)}`, 5000);
                }
            };
            // 禁言/解除：服务端写 room_mutes 并广播 player:muted，目标客户端
            // 立即闭麦并锁定麦克风；禁言持续到房间清空（全员退出自动解除）。
            const toggleMute = async (row: MemberRow): Promise<void> => {
                try {
                    const muted = row.muted !== true;
                    await this.opts.api(`${base}/mutes`, 'POST',
                        { userId: row.userId, muted });
                    this.ui.showHint(muted ? `已禁言 ${row.username}` : `已解除 ${row.username} 的禁言`);
                    refreshMicSoon();
                } catch (err) {
                    this.ui.showHint(`操作失败：${errorMessage(err)}`, 5000);
                }
            };
            // 开麦：主持人远程打开该成员的麦克风——服务端先清除其禁言（等同
            // 解除禁言），再定向下发 player:mic-open，目标端立即开麦并提示。
            const openMic = async (row: MemberRow): Promise<void> => {
                try {
                    await this.opts.api(`${base}/mic-open`, 'POST', { userId: row.userId });
                    this.ui.showHint(`已让 ${row.username} 开麦`);
                    refreshMicSoon();
                } catch (err) {
                    this.ui.showHint(`操作失败：${errorMessage(err)}`, 5000);
                }
            };
            // 关麦：主持人远程关闭该成员的麦克风——定向下发 player:mic-close，
            // 目标端立即闭麦（一次性动作，不写禁言，仍可自行重新开麦）。
            const closeMic = async (row: MemberRow): Promise<void> => {
                try {
                    await this.opts.api(`${base}/mic-close`, 'POST', { userId: row.userId });
                    this.ui.showHint(`已让 ${row.username} 关麦`);
                    refreshMicSoon();
                } catch (err) {
                    this.ui.showHint(`操作失败：${errorMessage(err)}`, 5000);
                }
            };
            // ── 成员渲染：本地合成（快照 + 30Hz 状态）为主，HTTP 校准为辅 ──
            // main.ts 注入的 getLocalMembers 从 RemotePlayers/语音状态本地组装，
            // 打开面板与每次状态变化零延迟渲染（mic/禁言/进出房经 socket 事件
            // 实时翻转，不再等 5s HTTP 轮询）；HTTP /members 降为 15s 后台校准，
            // 兜底重连等场景漏事件造成的漂移。渲染按 userId 排序 + 序列化比对，
            // 两个数据源顺序一致、无变化不重建 DOM（不打断悬停/点击中的按钮）。
            let membersRenderKey = '';
            const renderMembers = (rows: MemberRow[]): void => {
                const sorted = [...rows].sort((a, b) => a.userId - b.userId);
                const key = JSON.stringify(sorted);
                if (key === membersRenderKey) return;
                membersRenderKey = key;
                anyMuted = sorted.some((row) => row.muted === true);
                syncMuteAll();
                memberList.replaceChildren(...sorted.map((row) => {
                    const item = h('div', 'wh-row');
                    if (row.userId === this.opts.user.id) item.classList.add('wh-self');
                    const info = h('div', 'wh-info');
                    info.append(h('span', 'wh-name', row.username));
                    const meta = [row.organization, `${levelTitle(row.level)} ${row.level}级`, statusText(row.status), row.muted === true ? '已禁言' : row.mic === true ? '开麦中' : '']
                        .filter(Boolean).join(' · ');
                    info.append(h('span', 'wh-meta', meta));
                    const actions = h('div', 'wh-actions');
                    // 开麦/关麦：随该成员实时 mic 状态切换（本地 30Hz 合成，
                    // 操作后经目标端状态上行 ~0.1-1s 内翻转）。自己那行禁用——
                    // 自行开麦走语音面板更直接。
                    const micOn = row.mic === true;
                    const unmuteMic = h('button', micOn ? 'wh-mic-close' : 'wh-mic-open',
                        micOn ? '关麦' : '开麦');
                    if (row.userId === this.opts.user.id) {
                        unmuteMic.disabled = true;
                        unmuteMic.title = '可在语音面板自行开关麦';
                    }
                    unmuteMic.addEventListener('click', () => void (micOn ? closeMic(row) : openMic(row)));
                    // 禁言/解除禁言：目标麦克风锁定为闭麦，可随时解除。
                    const mute = h('button', row.muted === true ? 'wh-unmute' : 'wh-mute',
                        row.muted === true ? '解除禁言' : '禁言');
                    if (row.userId === this.opts.user.id) {
                        mute.disabled = true;
                        mute.title = '不能禁言自己';
                    }
                    mute.addEventListener('click', () => void toggleMute(row));
                    const btn = h('button', 'wh-kick', '踢出');
                    if (row.userId === this.opts.user.id) {
                        btn.disabled = true;
                        btn.title = '不能踢出自己';
                    }
                    btn.addEventListener('click', () => void kick(row));
                    actions.append(unmuteMic, mute, btn);
                    item.append(info, actions);
                    return item;
                }));
                if (!sorted.length) memberList.append(h('p', 'wf-empty', '房间内暂无成员。'));
            };
            const syncMembersLocal = (): void => {
                if (memberPane.classList.contains('hidden')) return;
                const rows = this.opts.getLocalMembers?.();
                if (rows) renderMembers(rows);
            };
            // 操作后短窗本地重刷几次：开/关麦要经目标端「收到事件 → 切换麦克
            // 风 → 30Hz 状态上行」回路，~0.1-1s 内翻转；目标端始终未生效（无
            // 语音/权限拒绝）时保持真实的未开麦态，不假翻转。纯本地组装零请求。
            const micTimers: number[] = [];
            const refreshMicSoon = (): void => {
                syncMembersLocal();
                for (const delay of [400, 1000])
                    micTimers.push(window.setTimeout(syncMembersLocal, delay));
            };
            // HTTP 校准：拉取乱序守卫，旧响应晚到不得覆盖新数据；失败不打断
            // 本地渲染（仅无本地数据源时才显示错误占位）。
            let membersSeq = 0;
            const loadMembers = async (): Promise<void> => {
                const seq = ++membersSeq;
                try {
                    const rows = await this.opts.api<MemberRow[]>(`${base}/members`);
                    if (seq !== membersSeq) return;
                    renderMembers(rows);
                } catch (err) {
                    if (!this.opts.getLocalMembers)
                        memberList.replaceChildren(h('p', 'wf-empty', `成员加载失败：${errorMessage(err)}`));
                }
            };
            const localTimer = window.setInterval(syncMembersLocal, 1000);
            const calibTimer = window.setInterval(() => void loadMembers(), 15000);
            register(() => {
                window.clearInterval(localTimer);
                window.clearInterval(calibTimer);
                for (const t of micTimers) window.clearTimeout(t);
            });
            syncMembersLocal();
            void loadMembers();

            // ── 禁入名单：查看 / 解除禁入（会话级缓存 + 后台刷新）──
            const banList = h('div', 'wh-list');
            banPane.append(banList);
            const renderBans = (rows: BanRow[]): void => {
                banList.replaceChildren(...rows.map((row) => {
                    const item = h('div', 'wh-row');
                    const info = h('div', 'wh-info');
                    info.append(h('span', 'wh-name', row.username));
                    if (row.reason) info.append(h('span', 'wh-meta', row.reason));
                    const actions = h('div', 'wh-actions');
                    const btn = h('button', 'wh-unban', '解除禁入');
                    btn.addEventListener('click', async () => {
                        try {
                            await this.opts.api(`${base}/bans/${row.user_id}`, 'DELETE');
                            this.ui.showHint(`已解除 ${row.username} 的禁入`);
                            // 乐观更新缓存先行移除该行，再后台拉全量校准。
                            this.banCache = (this.banCache ?? rows).filter((b) => b.user_id !== row.user_id);
                            renderBans(this.banCache);
                            void loadBans();
                        } catch (err) {
                            this.ui.showHint(`操作失败：${errorMessage(err)}`, 5000);
                        }
                    });
                    actions.append(btn);
                    item.append(info, actions);
                    return item;
                }));
                if (!rows.length) banList.append(h('p', 'wf-empty', '暂无禁入记录。'));
            };
            const loadBans = async (): Promise<void> => {
                if (this.banCache) renderBans(this.banCache);
                try {
                    const rows = await this.opts.api<BanRow[]>(`${base}/bans`);
                    this.banCache = rows;
                    renderBans(rows);
                } catch (err) {
                    if (!this.banCache) banList.replaceChildren(h('p', 'wf-empty', `禁入名单加载失败：${errorMessage(err)}`));
                }
            };

            // ── 物体（P3a）：内置模型库选模型 → 面前放置；已放置列表可删除 ──
            // 放置/删除经 socket 命令（main.ts 注入回调），全员实时同步，
            // 后进房/重连自动恢复；全员退出后清空（人走灯灭）。
            const modelsGrid = h('div', 'wh-model-grid');
            const placeBtn = h('button', 'wc-confirm wh-place-btn', '放置');
            placeBtn.disabled = true;
            const objectList = h('div', 'wh-list wh-object-list');
            let pickedModel: ModelRow | null = null;
            let modelRows: ModelRow[] = [];
            const syncPicked = () => {
                modelsGrid.querySelectorAll('.wh-model-card').forEach((n) =>
                    n.classList.toggle('picked', (n as HTMLElement).dataset.modelId === pickedModel?.id));
                placeBtn.disabled = !pickedModel;
            };
            const renderModels = (rows: ModelRow[]): void => {
                modelRows = rows;
                modelsGrid.replaceChildren(...rows.map((m) => {
                    const card = h('button', 'wh-model-card');
                    card.dataset.modelId = m.id;
                    card.title = '点击选中模型';
                    const thumb = h('div', 'wh-model-thumb');
                    if (m.thumbUrl) {
                        const img = h('img');
                        img.alt = '';
                        img.decoding = 'async';
                        img.addEventListener('error', () => img.remove());
                        img.src = m.thumbUrl;
                        thumb.append(img);
                    }
                    thumb.append(h('span', '', m.name.slice(0, 1)));
                    card.append(thumb, h('span', 'wh-model-name', m.name));
                    card.addEventListener('click', () => { pickedModel = m; syncPicked(); });
                    return card;
                }));
                if (!rows.length) modelsGrid.append(h('p', 'wf-empty', '模型库为空：把 GLB 放进 assets/models/ 目录后重启服务即上架。'));
                syncPicked();
            };
            const loadModels = async (): Promise<void> => {
                if (this.modelCache) renderModels(this.modelCache);
                try {
                    const rows = await this.opts.api<ModelRow[]>('/hub/models');
                    renderModels(rows);
                    this.modelCache = rows;
                } catch (err) {
                    if (!this.modelCache) modelsGrid.replaceChildren(h('p', 'wf-empty', `模型库加载失败：${errorMessage(err)}`));
                }
            };
            const loadRoomObjects = async (): Promise<void> => {
                try {
                    const data = await this.opts.api<{ objects: { id: string; name: string }[] }>(
                        `/hub/rooms/${encodeURIComponent(roomId)}/objects`);
                    objectList.replaceChildren(...(data.objects ?? []).map((o) => {
                        const item = h('div', 'wh-row');
                        const info = h('div', 'wh-info');
                        info.append(h('span', 'wh-name', o.name || '未命名物体'));
                        info.append(h('span', 'wh-meta', o.id));
                        const actions = h('div', 'wh-actions');
                        // 移动：关闭面板进入 Gizmo 编辑会话（W 移动 / E 旋转，松手同步全员）。
                        const edit = h('button', 'wh-move', '移动');
                        edit.addEventListener('click', () => {
                            this.closeFullscreen();
                            this.opts.onEditObject?.(o.id);
                        });
                        const btn = h('button', 'wh-kick', '删除');
                        btn.addEventListener('click', async () => {
                            try {
                                await this.opts.onRemoveObject?.(o.id);
                                this.ui.showHint('已删除物体');
                                void loadRoomObjects();
                            } catch (err) {
                                this.ui.showHint(`删除失败：${err instanceof Error ? err.message : errorMessage(err)}`, 5000);
                            }
                        });
                        actions.append(edit, btn);
                        item.append(info, actions);
                        return item;
                    }));
                    if (!data.objects?.length) objectList.append(h('p', 'wf-empty', '尚未放置物体。'));
                } catch (err) {
                    objectList.replaceChildren(h('p', 'wf-empty', `物体列表加载失败：${errorMessage(err)}`));
                }
            };
            placeBtn.addEventListener('click', () => {
                if (!pickedModel) return;
                this.closeFullscreen(); // 释放指针，按镜头朝向在面前落地
                this.opts.onPlaceObject?.(pickedModel);
            });
            const loadObjectsTab = async (): Promise<void> => {
                await Promise.all([loadModels(), loadRoomObjects()]);
            };
            objectPane.append(
                h('p', 'wh-obj-note', '选择模型后点「放置」：物体将出现在你面前 3 米的地面并同步给房间所有人；全员退出房间后物体自动清空。'),
                modelsGrid,
                placeBtn,
                h('div', 'wh-subhead', '已放置物体'),
                objectList);

            // ── 音乐：全场背景音乐（服务端权威同步，主持人操作含本人按
            //    room:music 事件回环应用，不本地先行）──
            // 状态行轮询 main.ts 的 getMusicState（事件驱动的最新同步态），
            // 1s 刷新播放/暂停文案与音量滑杆；曲目库走 GET /hub/tracks。
            const musicControls = h('div', 'wh-music-controls');
            const musicStatus = h('span', 'wh-music-status', '未播放');
            const musicToggle = h('button', 'wh-music-toggle', '暂停');
            const musicStop = h('button', 'wh-music-stop', '停止');
            musicControls.append(musicStatus, musicToggle, musicStop);
            const volumeRow = h('div', 'wh-music-volume');
            const volumeSlider = h('input', 'wh-music-slider');
            volumeSlider.type = 'range';
            volumeSlider.min = '0';
            volumeSlider.max = '100';
            volumeSlider.step = '1';
            const volumeValue = h('span', 'wh-music-volume-value', '60%');
            volumeRow.append(h('span', 'wh-music-volume-label', '全场音量'), volumeSlider, volumeValue);
            const trackList = h('div', 'wh-list wh-music-list');
            let musicState = this.opts.getMusicState?.() ?? null;
            const syncMusicUI = (): void => {
                musicState = this.opts.getMusicState?.() ?? musicState;
                const active = !!musicState && musicState.trackId !== '';
                musicStatus.textContent = active
                    ? `${musicState!.playing ? '正在播放' : '已暂停'}：${musicState!.trackName || musicState!.trackId}`
                    : '未播放';
                musicToggle.textContent = active && musicState!.playing ? '暂停' : '继续';
                musicToggle.disabled = !active;
                musicStop.disabled = !active;
                // 拖动中不回写滑杆（避免与用户手指/光标打架），松手后恢复同步。
                if (document.activeElement !== volumeSlider) {
                    const pct = Math.round((musicState?.volume ?? 0.6) * 100);
                    volumeSlider.value = String(pct);
                    volumeValue.textContent = `${pct}%`;
                }
            };
            musicToggle.addEventListener('click', () => {
                void this.opts.onMusicControl?.(musicState?.playing ? 'pause' : 'resume')
                    .catch(err => this.ui.showHint(`操作失败：${errorMessage(err)}`, 5000));
            });
            musicStop.addEventListener('click', async () => {
                const ok = await this.ui.showConfirm('确定停止背景音乐？全场将恢复安静。', '停止播放', '再想想');
                if (!ok) return;
                void this.opts.onMusicControl?.('stop')
                    .then(() => this.ui.showHint('已停止背景音乐'))
                    .catch(err => this.ui.showHint(`操作失败：${errorMessage(err)}`, 5000));
            });
            // 音量：拖动过程中 input 300ms 节流上行（即时反馈，且部分移动端
            // range 的 change 事件不可靠、松手不触发——以 input 为主通道），
            // change 只作终值兜底；服务端幂等（多次下发只是多几个 seq）。
            let volumeSentAt = 0;
            const sendVolume = (): void => {
                volumeSentAt = Date.now();
                void this.opts.onMusicControl?.('volume', undefined, Number(volumeSlider.value) / 100)
                    .catch(err => this.ui.showHint(`音量设置失败：${errorMessage(err)}`, 5000));
            };
            volumeSlider.addEventListener('input', () => {
                volumeValue.textContent = `${volumeSlider.value}%`;
                if (Date.now() - volumeSentAt >= 300) sendVolume();
            });
            volumeSlider.addEventListener('change', () => sendVolume());
            const renderTracks = (rows: TrackRow[]): void => {
                trackList.replaceChildren(...rows.map((t) => {
                    const item = h('div', 'wh-row');
                    const info = h('div', 'wh-info');
                    info.append(h('span', 'wh-name', t.name));
                    const actions = h('div', 'wh-actions');
                    const play = h('button', 'wh-music-play', '播放');
                    play.addEventListener('click', () => {
                        void this.opts.onMusicControl?.('play', t.id)
                            .then(() => this.ui.showHint(`已播放「${t.name}」`))
                            .catch(err => this.ui.showHint(`播放失败：${errorMessage(err)}`, 5000));
                    });
                    actions.append(play);
                    item.append(info, actions);
                    return item;
                }));
                if (!rows.length) trackList.append(h('p', 'wf-empty', '曲目库为空：把音频文件（mp3/ogg/wav/m4a）放进 assets/audio/ 目录后重启服务即上架。'));
            };
            const loadTracks = async (): Promise<void> => {
                if (this.trackCache) renderTracks(this.trackCache);
                try {
                    const rows = await this.opts.api<TrackRow[]>('/hub/tracks');
                    this.trackCache = rows;
                    renderTracks(rows);
                } catch (err) {
                    if (!this.trackCache) trackList.replaceChildren(h('p', 'wf-empty', `曲目库加载失败：${errorMessage(err)}`));
                }
            };
            const loadMusicTab = async (): Promise<void> => {
                syncMusicUI();
                await loadTracks();
            };
            const musicTimer = window.setInterval(() => {
                if (!musicPane.classList.contains('hidden')) syncMusicUI();
            }, 1000);
            register(() => window.clearInterval(musicTimer));
            musicPane.append(
                h('p', 'wh-obj-note', '播放后全场成员同步收听同一背景音乐（与玩家位置无关）；换曲/重播从头开始，音量对全场统一生效。'),
                musicControls,
                volumeRow,
                h('div', 'wh-subhead', '曲目库'),
                trackList);

            // ── 口令管理（仅平台管理员页签可达）：查看/修改主持人、
            // 副主持人口令。两口令独立：主持人口令得主持人席位、副主持
            // 人口令得副主持人席位，席位各自唯一（后输入者顶替同席前任）。
            const codeShow = h('div', 'wh-code', '加载中…');
            const cohostShow = h('div', 'wh-code', '加载中…');
            const codeGet = async (key: 'host-grant-code' | 'cohost-grant-code') => {
                const s = await this.opts.api<{ code: string }>(`/admin/settings/${key}`);
                return s.code;
            };
            const loadCode = async (): Promise<void> => {
                try {
                    codeShow.textContent = await codeGet('host-grant-code');
                } catch (err) {
                    codeShow.textContent = `加载失败：${errorMessage(err)}`;
                }
                try {
                    const code = await codeGet('cohost-grant-code');
                    cohostShow.textContent = code || '未设置（副主持人授权未启用）';
                } catch (err) {
                    cohostShow.textContent = `加载失败：${errorMessage(err)}`;
                }
            };
            codePane.append(
                h('p', 'wh-note',
                    '主持人口令全局通用：房间成员经 🛡 按钮输入正确口令即获得该房间的主持人权限。'),
                codeShow,
                h('p', 'wh-note',
                    '副主持人口令全局通用：输入正确口令即获得该房间的副主持人权限；主持人与副主持人席位相互独立、各自唯一，后输入者顶替同席前任。'),
                cohostShow);
            const codeField = (label: string, endpoint: string, hint: string): HTMLElement => {
                const field = h('label', 'wc-field');
                field.append(h('span', '', label));
                const input = h('input');
                input.type = 'text';
                input.maxLength = 64;
                input.autocomplete = 'off';
                input.placeholder = '输入新口令';
                field.append(input);
                const msg = h('p', 'wc-msg');
                const actions = h('div', 'wshare-actions');
                const save = h('button', 'wshare-btn', '保存口令');
                save.addEventListener('click', async () => {
                    const code = input.value.trim();
                    if (code.length < 5) { msg.textContent = '口令至少 5 位。'; return; }
                    save.disabled = true;
                    msg.textContent = '正在保存…';
                    try {
                        const s = await this.opts.api<{ code: string }>(
                            `/admin/settings/${endpoint}`, 'PUT', { code });
                        input.value = '';
                        msg.textContent = '';
                        this.ui.showHint(hint);
                        if (endpoint === 'host-grant-code') codeShow.textContent = s.code;
                        else cohostShow.textContent = s.code;
                    } catch (err) {
                        msg.textContent = `保存失败：${errorMessage(err)}`;
                    } finally {
                        save.disabled = false;
                    }
                });
                actions.append(save);
                const wrap = h('div');
                wrap.append(field, actions, msg);
                return wrap;
            };
            if (isAdmin) {
                codePane.append(
                    h('p', 'wh-note', '修改后立即对全部房间生效，已授权的主持人不受影响。'),
                    codeField('新主持人口令（5-64 位）', 'host-grant-code', '主持人口令已更新，即时生效'),
                    h('p', 'wh-note', '副主持人口令修改后立即对全部房间生效，已授权的副主持人不受影响。'),
                    codeField('新副主持人口令（5-64 位）', 'cohost-grant-code', '副主持人口令已更新，即时生效'));
            }
        });
    }

    // ── Shared chrome: fullscreen panel + small modal ──────────────────────

    /** Fullscreen panel (世界场景/创建场景/加入场景/角色). build() fills the
     *  body; optional register(fn) collects cleanup (3D app destroy, timers).
     *  headerExtra：可选的头部附加元素（如角色的「退出登录」按钮），
     *  插在标题之后、关闭按钮之前。 */
    private _openFullscreen(title: string, icon: string, build: (body: HTMLElement, register: (fn: () => void) => void) => void, headerExtra?: HTMLElement): void {
        this.closeFullscreen();
        this.closeModal();
        this.closeMenu();
        const panel = h('div');
        panel.id = 'world-fullscreen';
        const box = h('div', 'wf-box');
        const header = h('div', 'wf-header');
        header.append(h('span', 'wf-icon', icon), h('span', 'wf-title', title));
        const close = h('button', 'wf-close', '×');
        if (headerExtra) header.append(headerExtra);
        header.append(close);
        const body = h('div', 'wf-body');
        box.append(header, body);
        panel.append(box);
        const layer = document.getElementById('ui-layer') ?? document.body;
        layer.appendChild(panel);
        close.addEventListener('click', () => this.closeFullscreen());
        panel.addEventListener('click', (e) => { if (e.target === panel) this.closeFullscreen(); });
        this.fullscreen = panel;
        this.fullscreenCleanups = [];
        build(body, (fn) => this.fullscreenCleanups.push(fn));
        this.ui.requestUnlock?.();
        this.ui.onPopupOpen?.();
    }

    closeFullscreen(): void {
        if (!this.fullscreen) return;
        const cleanups = this.fullscreenCleanups;
        this.fullscreenCleanups = [];
        cleanups.splice(0).forEach((fn) => { try { fn(); } catch { /* ignore */ } });
        this.fullscreen.remove();
        this.fullscreen = null;
        this.ui.onPopupClose?.();
    }

    /** Small centered modal (分享/公告/反馈). */
    private _openModal(title: string): HTMLElement {
        this.closeModal();
        this.closeMenu();
        const panel = h('div');
        panel.id = 'world-modal';
        const card = h('div', 'wm-modal-card');
        const close = h('button', 'wm-modal-close', '×');
        card.append(close, h('h3', '', title));
        const body = h('div', 'wm-modal-body');
        card.append(body);
        panel.append(card);
        const layer = document.getElementById('ui-layer') ?? document.body;
        layer.appendChild(panel);
        close.addEventListener('click', () => this.closeModal());
        panel.addEventListener('click', (e) => { if (e.target === panel) this.closeModal(); });
        this.modal = panel;
        this.ui.requestUnlock?.();
        this.ui.onPopupOpen?.();
        return body;
    }

    closeModal(): void {
        if (!this.modal) return;
        this.modal.remove();
        this.modal = null;
        this.ui.onPopupClose?.();
    }

    // ── Data helpers ───────────────────────────────────────────────────────

    private async _rooms(): Promise<RoomRow[]> {
        return this.opts.api<RoomRow[]>('/hub/rooms');
    }

    private async _joinRoom(roomId: string): Promise<void> {
        // 进房前预检人数：满员直接提示，不整页跳转（服务端 room:join 仍是最终裁决）。
        // 房主/管理员与创建者豁免满员（服务端行为一致），预检同样放行。
        try {
            const target = (await this._rooms()).find((r) => r.id === roomId);
            if (target && !target.isHost && target.occupiedCount >= target.maxParticipants) {
                this.ui.showHint('人数已满，请过半小时后再加入。', 5000);
                return;
            }
        } catch { /* 列表拉取失败不阻塞，交给服务端校验 */ }
        try {
            const t = await this.opts.api<{ roomId: string; sceneId: string; invite: string }>(
                `/hub/rooms/${encodeURIComponent(roomId)}/invite`, 'POST');
            this.opts.onJoinRoom(t);
        } catch (err) {
            this.ui.showHint(`进入房间失败：${errorMessage(err)}`, 5000);
        }
    }

    /** Simple footer pager: ‹ prev / page info / next ›. */
    private _pager(footer: HTMLElement, perPage: number, show: (page: number) => void): { setTotal(total: number): void } {
        let page = 1;
        let pages = 1;
        const prev = h('button', 'wf-pager-btn', '‹');
        const next = h('button', 'wf-pager-btn', '›');
        const info = h('span', 'wf-pager-info');
        const sync = () => {
            info.textContent = `${page} / ${pages}`;
            prev.disabled = page <= 1;
            next.disabled = page >= pages;
        };
        prev.addEventListener('click', () => { if (page > 1) { page--; sync(); show(page); } });
        next.addEventListener('click', () => { if (page < pages) { page++; sync(); show(page); } });
        footer.append(prev, info, next);
        return {
            setTotal(total: number) {
                pages = Math.max(1, Math.ceil(total / perPage));
                if (page > pages) page = pages;
                sync();
                show(page);
            }
        };
    }

    private _pageOf<T>(items: T[], page: number, perPage: number): T[] {
        return items.slice((page - 1) * perPage, page * perPage);
    }

    // ── 世界场景（离线单人漫游：只列场景，无房间/人数等联机数据）────────────

    private async openWorld(): Promise<void> {
        let scenes: { id: string; name: string }[];
        try { scenes = await this.opts.api<{ id: string; name: string }[]>('/hub/scenes'); }
        catch (err) { this.ui.showHint(`场景列表加载失败：${errorMessage(err)}`); return; }
        this._openFullscreen('世界场景', '🌍', (body) => {
            body.append(h('p', 'ws-solo-note',
                '离线单人漫游：不进房间 · 不连服务器 · 不同步其他玩家，场景中只有你一人。点击卡片选中场景，再点下方「进入」开始漫游。'));
            const grid = h('div', 'ws-grid ws-grid-scene');
            const footer = h('div', 'wf-footer');
            let pickedId: string | null = null;
            let page = 1;
            const enter = h('button', 'wc-confirm', '进入');
            enter.disabled = true;
            // 原地切换选中态，不重建网格，避免缩略图重新加载闪烁。
            const syncPicked = () => {
                grid.querySelectorAll('.ws-card').forEach((n) =>
                    n.classList.toggle('picked', (n as HTMLElement).dataset.sceneId === pickedId));
            };
            const pick = (id: string) => { pickedId = id; enter.disabled = false; syncPicked(); };
            const render = () => {
                grid.replaceChildren(...this._pageOf(scenes, page, 6).map((scene) => this._sceneCard(scene, scene.id === pickedId, pick)));
                if (!scenes.length) grid.append(h('p', 'wf-empty', '暂无可用场景，敬请期待。'));
            };
            const pager = this._pager(footer, 6, (p) => { page = p; render(); });
            enter.addEventListener('click', () => { if (pickedId) this.opts.onEnterScene(pickedId); });
            // 「进入」按钮独立成行，放在网格下方水平居中；footer 只留分页器。
            const enterRow = h('div', 'ws-enter-row');
            enterRow.append(enter);
            body.append(grid, footer, enterRow);
            pager.setTotal(scenes.length);
        });
    }

    /** 场景卡片：缩略图 + 场景名；点击仅选中，「进入」按钮才真正进入。 */
    private _sceneCard(scene: { id: string; name: string }, picked: boolean, onPick: (id: string) => void): HTMLElement {
        const card = h('button', 'ws-card');
        if (picked) card.classList.add('picked');
        card.title = '点击选中';
        card.dataset.sceneId = scene.id;
        card.append(sceneThumb(scene.id, scene.name), h('div', 'ws-name', scene.name));
        card.addEventListener('click', () => onPick(scene.id));
        return card;
    }

    // ── 加入场景（联机房间）──────────────────────────────────────────────────

    private async openJoin(): Promise<void> {
        let rooms: RoomRow[];
        try { rooms = await this._rooms(); }
        catch (err) { this.ui.showHint(`房间列表加载失败：${errorMessage(err)}`); return; }
        this._openFullscreen('加入场景', '🚪', (body) => {
            const grid = h('div', 'ws-grid');
            const footer = h('div', 'wf-footer');
            let pickedId: string | null = null;
            let page = 1;
            const enter = h('button', 'wc-confirm', '进入');
            enter.disabled = true;
            const refresh = h('button', 'wj-refresh', '⟳ 刷新');
            refresh.addEventListener('click', () => { this.closeFullscreen(); void this.openJoin(); });
            const pick = (id: string) => {
                pickedId = id;
                enter.disabled = false;
                // 原地切换选中态，不重建网格，避免缩略图重新加载闪烁。
                grid.querySelectorAll('.ws-card').forEach((n) =>
                    n.classList.toggle('picked', (n as HTMLElement).dataset.roomId === pickedId));
            };
            const render = () => {
                grid.replaceChildren(...this._pageOf(rooms, page, 6).map((room) => this._worldCard(room, room.id === pickedId, pick)));
                if (!rooms.length) grid.append(h('p', 'wf-empty', '暂无开放房间，可以自己创建一个。'));
            };
            // 刷新按钮放在分页器（‹）左侧，进入按钮在最后。
            footer.append(refresh);
            const pager = this._pager(footer, 6, (p) => { page = p; render(); });
            enter.addEventListener('click', () => { if (pickedId) void this._joinRoom(pickedId); });
            footer.append(enter);
            body.append(grid, footer);
            pager.setTotal(rooms.length);
        });
    }

    /** 房间卡片：与「世界场景」同款 ws-card，人数后附创建人；创建时间另起一行
     *  左对齐。点击仅选中，「进入」按钮才真正进房。 */
    private _worldCard(room: RoomRow, picked: boolean, onPick: (id: string) => void): HTMLElement {
        const card = h('button', 'ws-card');
        if (picked) card.classList.add('picked');
        card.title = '点击选中';
        card.dataset.roomId = room.id;
        const thumb = sceneThumb(room.sceneId, room.sceneName);
        const name = h('div', 'ws-name', room.name);
        const scene = h('div', 'ws-scene', room.sceneName);
        const meta = h('div', 'ws-meta');
        meta.append(h('span', 'ws-tag', room.hasPassword ? '加密' : '公共'));
        meta.append(h('span', 'ws-people', `${room.occupiedCount}/${room.maxParticipants} 人`));
        if (room.hostName) meta.append(h('span', 'ws-host', room.hostName));
        if (room.id === this.opts.currentRoom.roomId) meta.append(h('span', 'wj-current', '当前房间'));
        card.append(thumb, name, scene, meta);
        const createdText = formatRoomTime(room.createdAt);
        if (createdText) card.append(h('div', 'ws-time-line', `创建于 ${createdText}`));
        card.addEventListener('click', () => onPick(room.id));
        return card;
    }

    // ── 创建场景 ────────────────────────────────────────────────────────────

    private async openCreate(): Promise<void> {
        let scenes: { id: string; name: string }[];
        try { scenes = await this.opts.api<{ id: string; name: string }[]>('/hub/scenes'); }
        catch (err) { this.ui.showHint(`场景模板加载失败：${errorMessage(err)}`); return; }
        if (!scenes.length) { this.ui.showHint('暂无可用场景模板'); return; }
        // 「我的」页签数据：自己创建的开放房间；拉取失败不阻塞「新建」页签。
        let myRooms: RoomRow[] = [];
        try { myRooms = (await this._rooms()).filter((r) => r.isHost); } catch { /* ignore */ }
        this._openFullscreen('创建场景', '🏗️', (body) => {
            // ── 页签骨架：新建 / 我的（左缩进与轮播卡片对齐） ──
            const tabs = h('div', 'wf-tabs wc-tabs');
            const createPane = h('div', 'wc-pane');
            const minePane = h('div', 'wc-pane hidden');
            const tabBtns = {
                create: h('button', 'wf-tab active', '新建'),
                mine: h('button', 'wf-tab', '我的')
            };
            const syncTabs = (active: 'create' | 'mine') => {
                for (const key of ['create', 'mine'] as const) tabBtns[key].classList.toggle('active', key === active);
                createPane.classList.toggle('hidden', active !== 'create');
                minePane.classList.toggle('hidden', active !== 'mine');
            };
            tabBtns.create.addEventListener('click', () => syncTabs('create'));
            tabBtns.mine.addEventListener('click', () => syncTabs('mine'));
            tabs.append(tabBtns.create, tabBtns.mine);
            body.append(tabs, createPane, minePane);

            // 房间设置表单（两页签同款布局）。
            const buildForm = () => {
                const form = h('div', 'wc-form');
                const formRow = h('div', 'wc-form-row');
                const nameRow = h('label', 'wc-field');
                nameRow.append(h('span', '', '房间名称'));
                const name = h('input');
                name.type = 'text';
                name.maxLength = 100;
                nameRow.append(name);
                const maxRow = h('label', 'wc-field');
                maxRow.append(h('span', '', '最大人数'));
                const maxWrap = h('div', 'wc-max-wrap');
                const max = h('input');
                max.type = 'range';
                max.min = '2';
                max.max = '200';
                max.step = '1';
                max.value = '20';
                const maxVal = h('span', 'wc-max-val', '20');
                max.addEventListener('input', () => { maxVal.textContent = max.value; });
                maxWrap.append(max, maxVal);
                maxRow.append(maxWrap);
                const passRow = h('label', 'wc-field');
                passRow.append(h('span', '', '房间密码（可选）'));
                const pass = h('input');
                pass.type = 'password';
                pass.maxLength = 64;
                pass.autocomplete = 'new-password';
                pass.placeholder = '留空则无需密码';
                // 「我的」页签：勾选后清除原密码（密码哈希不可回填，仅显示设置状态）。
                const clearWrap = h('div', 'wc-check hidden');
                const clearBox = h('input');
                clearBox.type = 'checkbox';
                clearWrap.append(clearBox, h('span', '', '清除原密码'));
                passRow.append(pass, clearWrap);
                const msg = h('p', 'wc-msg');
                formRow.append(nameRow, maxRow, passRow);
                form.append(formRow, msg);
                return { form, name, max, maxVal, pass, clearWrap, clearBox, msg };
            };

            // ════ 新建页签：模板选择 + 确认创建 ════
            {
                let sceneId = scenes[0].id;
                const cards = scenes.map((s) => {
                    const card = h('button', 'wc-card');
                    const thumb = sceneThumb(s.id, s.name);
                    card.append(thumb, h('div', 'ws-name', s.name));
                    const pick = () => {
                        sceneId = s.id;
                        createPane.querySelectorAll('.wc-card').forEach((n) => n.classList.remove('picked'));
                        card.classList.add('picked');
                        if (!form.name.value.trim() || scenes.some((x) => x.name === form.name.value.trim())) form.name.value = s.name;
                    };
                    card.addEventListener('click', pick);
                    return card;
                });
                cards[0].classList.add('picked');
                const carousel = carouselOf(cards);
                const form = buildForm();
                form.name.value = scenes[0].name;
                const actions = h('div', 'wf-footer wc-actions');
                const confirm = h('button', 'wc-confirm', '确认');
                confirm.addEventListener('click', async () => {
                    const roomName = form.name.value.trim();
                    if (!roomName) { form.msg.textContent = '请填写房间名称'; return; }
                    confirm.disabled = true;
                    form.msg.textContent = '正在创建…';
                    try {
                        const payload: Record<string, unknown> = {
                            name: roomName,
                            sceneId,
                            maxParticipants: Number(form.max.value)
                        };
                        if (form.pass.value) payload.password = form.pass.value;
                        const created = await this.opts.api<{ roomId: string; sceneId: string; invite: string }>('/hub/rooms', 'POST', payload);
                        this.opts.onJoinRoom(created);
                    } catch (err) {
                        form.msg.textContent = errorMessage(err);
                        confirm.disabled = false;
                    }
                });
                actions.append(confirm);
                createPane.append(carousel.root, form.form, actions);
            }

            // ════ 我的页签：历史创建的房间（选中回填 → 修改/重置）════
            if (!myRooms.length) {
                minePane.append(h('p', 'wf-empty', '您还没有创建过场景，可在「新建」页签创建。'));
            } else {
                const form = buildForm();
                form.name.placeholder = '请先选择上方房间卡片';
                let selected: RoomRow | null = null;
                let selectedSync: (() => void) | null = null;
                // 回填该房间历史设置（重置也走这里）。
                const fill = () => {
                    const room = selected;
                    if (!room) return;
                    form.name.value = room.name;
                    form.max.value = String(room.maxParticipants);
                    form.maxVal.textContent = String(room.maxParticipants);
                    form.pass.value = '';
                    form.pass.placeholder = room.hasPassword ? '已设置密码，留空保持不变' : '留空则无需密码';
                    form.clearWrap.classList.toggle('hidden', !room.hasPassword);
                    form.clearBox.checked = false;
                    form.msg.textContent = '';
                };
                const entries = myRooms.map((room) => {
                    const entry = this._myRoomCard(room, () => {
                        selected = room;
                        selectedSync = entry.sync;
                        minePane.querySelectorAll('.wc-card').forEach((n) => n.classList.remove('picked'));
                        entry.card.classList.add('picked');
                        save.disabled = false;
                        reset.disabled = false;
                        fill();
                    });
                    return entry;
                });
                const carousel = carouselOf(entries.map((e) => e.card));
                const actions = h('div', 'wf-footer wc-actions');
                const save = h('button', 'wc-confirm', '修改');
                const reset = h('button', 'wc-reset', '重置');
                save.disabled = true;
                reset.disabled = true;
                save.addEventListener('click', async () => {
                    const room = selected;
                    if (!room) return;
                    const roomName = form.name.value.trim();
                    if (!roomName) { form.msg.textContent = '请填写房间名称'; return; }
                    save.disabled = true;
                    form.msg.textContent = '正在保存…';
                    try {
                        const payload: Record<string, unknown> = {
                            name: roomName,
                            maxParticipants: Number(form.max.value)
                        };
                        if (form.clearBox.checked) payload.password = null;
                        else if (form.pass.value) payload.password = form.pass.value;
                        await this.opts.api(`/hub/rooms/${encodeURIComponent(room.id)}`, 'PATCH', payload);
                        room.name = roomName;
                        room.maxParticipants = Number(form.max.value);
                        if (form.clearBox.checked) room.hasPassword = false;
                        else if (form.pass.value) room.hasPassword = true;
                        selectedSync?.();
                        fill();
                        form.msg.textContent = '修改成功。';
                        this.ui.showHint('房间设置已更新');
                    } catch (err) {
                        form.msg.textContent = errorMessage(err);
                    } finally {
                        save.disabled = false;
                    }
                });
                reset.addEventListener('click', () => { if (selected) fill(); });
                actions.append(save, reset);
                minePane.append(carousel.root, form.form, actions);
            }
        });
    }

    /** 「我的」页签房间卡片：与新建页签 wc-card 同款样式；点击仅选中，
     *  由页签逻辑回填表单。sync() 在修改成功后原地刷新卡片文字。 */
    private _myRoomCard(room: RoomRow, onPick: () => void): { card: HTMLElement; sync: () => void } {
        const card = h('button', 'wc-card');
        card.title = '点击选中，下方可修改房间设置';
        const thumb = sceneThumb(room.sceneId, room.sceneName);
        const name = h('div', 'ws-name', room.name);
        const scene = h('div', 'ws-scene', room.sceneName);
        const meta = h('div', 'ws-meta');
        const tag = h('span', 'ws-tag', room.hasPassword ? '加密' : '公共');
        const people = h('span', 'ws-people', `${room.occupiedCount}/${room.maxParticipants} 人`);
        meta.append(tag, people);
        card.append(thumb, name, scene, meta);
        const createdText = formatRoomTime(room.createdAt);
        if (createdText) card.append(h('div', 'ws-time-line', `创建于 ${createdText}`));
        card.addEventListener('click', onPick);
        return {
            card,
            sync: () => {
                name.textContent = room.name;
                tag.textContent = room.hasPassword ? '加密' : '公共';
                people.textContent = `${room.occupiedCount}/${room.maxParticipants} 人`;
            }
        };
    }

    // ── 分享链接 ────────────────────────────────────────────────────────────

    private async openShare(): Promise<void> {
        const body = this._openModal('邀请加入');
        const { roomId, sceneId } = this.opts.currentRoom;
        let roomName = '';
        try { roomName = (await this._rooms()).find((r) => r.id === roomId)?.name ?? ''; } catch { /* name optional */ }
        body.append(h('p', 'wshare-sub', '邀请您加入南农元宇宙'));
        body.append(h('p', 'wshare-room', `房间主题：${roomName || roomId}`));
        body.append(h('p', 'wshare-label', '点击链接，加入场景：'));
        // 邀请令牌必须属于「当前房间」：currentRoom.invite 是 boot 时 entry 给
        // 默认房的 token（'/' resume 凭据路径则为空串），传送门换房后 onLoad 只
        // 更新 roomId/sceneId 不更新它——拿旧房 token 拼链接会被服务端
        // verifyInvite 按 roomId 查无此行直接拒（INVITE_INVALID，10-18 梅花山
        // 分享链接报障根因）。分享时现取：POST /hub/rooms/:id/invite 对开放
        // 房 get-or-create 本房有效令牌（同 _joinRoom 进房链路）；取失败退空
        // 令牌=游客链接语义（无校验可进公共房，比坏 token 可用）。
        let invite = '';
        if (roomId) {
            try {
                const t = await this.opts.api<{ roomId: string; sceneId: string; invite: string }>(
                    `/hub/rooms/${encodeURIComponent(roomId)}/invite`, 'POST');
                invite = t.invite ?? '';
                // 回写共享的 currentRoom（= business.invite）：同会话后续分享、
                // 其它读 currentRoom.invite 的路径拿到的是本房有效值。
                if (invite) this.opts.currentRoom.invite = invite;
            } catch { /* 拉取失败：空令牌退化为游客链接 */ }
        }
        const full = `${location.origin}/join?${new URLSearchParams({ roomId, sceneId, invite })}`;
        const url = h('div', 'wshare-url', full);
        body.append(url);
        const actions = h('div', 'wshare-actions');
        // 复制内容带上邀请文案与房间主题，方便直接粘贴到聊天工具。
        const shareText = (link: string) =>
            `邀请您加入南农元宇宙\n房间主题：${roomName || roomId}\n\n点击链接，加入场景：\n${link}`;
        const mkCopy = (label: string, text: string) => {
            const btn = h('button', 'wshare-btn', label);
            btn.addEventListener('click', async () => {
                try { await copyText(text); this.ui.showHint('链接已复制'); }
                catch { this.ui.showHint('复制失败，请手动选择链接复制'); }
            });
            return btn;
        };
        // 「复制游客链接」按 10-18 需求隐藏：只留邀请链接一个按钮（水平居中、
        // 内容宽度不拉伸，见 .wshare-actions/.wshare-btn）。
        actions.append(mkCopy('复制邀请链接', shareText(full)));
        body.append(actions);
    }

    // ── 公告 / 反馈 ────────────────────────────────────────────────────────

    /** 公告弹窗：所有人看公告列表；管理员额外在下方获得「输入框 + 发布按钮」，
     *  发布成功后原地刷新列表。风格沿用反馈表单（玻璃输入框 + wshare-btn）。 */
    private async openAnnounce(): Promise<void> {
        const body = this._openModal('公告');
        const isAdmin = this.opts.user.role === 'admin';
        const list = h('ul', 'wannounce');
        const box = h('div', 'wannounce-box');
        box.append(list);
        body.append(box);
        let area: HTMLTextAreaElement | null = null;
        let msg: HTMLElement | null = null;
        if (isAdmin) {
            area = h('textarea', 'wfeedback-area');
            area.rows = 4;
            area.placeholder = '输入要发布的公告内容…';
            const actions = h('div', 'wshare-actions');
            const publish = h('button', 'wshare-btn', '发布公告');
            msg = h('p', 'wc-msg');
            publish.addEventListener('click', async () => {
                const content = (area?.value ?? '').trim();
                if (!content) { msg!.textContent = '请输入公告内容。'; return; }
                publish.disabled = true;
                msg!.textContent = '正在发布…';
                try {
                    await this.opts.api('/admin/announcements', 'POST', { content });
                    area!.value = '';
                    msg!.textContent = '发布成功。';
                    this.ui.showHint('公告已发布');
                    await load();
                } catch (err) {
                    msg!.textContent = errorMessage(err);
                } finally {
                    publish.disabled = false;
                }
            });
            actions.append(publish);
            body.append(area, actions, msg);
        }
        const load = async (): Promise<void> => {
            try {
                const rows = await this.opts.api<AnnounceRow[]>('/hub/announcements');
                list.replaceChildren(...rows.map((r) => h('li', '', r.content)));
                if (!rows.length) list.append(h('li', '', '暂无公告。'));
            } catch (err) {
                list.replaceChildren(h('li', '', `公告加载失败：${errorMessage(err)}`));
            }
        };
        await load();
    }

    /** 反馈弹窗按角色分流：
     *  - 普通用户：表单提交反馈（POST /hub/feedbacks）；
     *  - 管理员：分页浏览所有用户反馈（反馈人 / 问题描述 / 提交时间），
     *    一页 6 条，列表在玻璃盒内滚动（滚动条与帮助页同款），底部 ‹ › 翻页。 */
    private async openFeedback(): Promise<void> {
        if (this.opts.user.role === 'admin') { await this._openFeedbackAdmin(); return; }
        const body = this._openModal('反馈');
        const tips = h('p', 'wf-empty', '遇到问题或有建议？写在下面告诉我们，管理员会在后台查看。');
        const area = h('textarea', 'wfeedback-area');
        area.rows = 5;
        area.placeholder = '请描述你遇到的问题或建议…';
        const actions = h('div', 'wshare-actions');
        const msg = h('p', 'wc-msg');
        const submit = h('button', 'wshare-btn', '提交反馈');
        submit.addEventListener('click', async () => {
            const content = area.value.trim();
            if (!content) { msg.textContent = '请输入反馈内容。'; return; }
            submit.disabled = true;
            msg.textContent = '正在提交…';
            try {
                await this.opts.api('/hub/feedbacks', 'POST', { content });
                area.value = '';
                msg.textContent = '已收到你的反馈，感谢！';
            } catch (err) {
                msg.textContent = errorMessage(err);
            } finally {
                submit.disabled = false;
            }
        });
        actions.append(submit);
        body.append(tips, area, actions, msg);
    }

    /** 管理员反馈列表：服务端分页（6 条/页），滚动容器与翻页器。
     *  不复用 _pager（其 setTotal 会回调 show 形成递归），这里独立维护页码。 */
    private async _openFeedbackAdmin(): Promise<void> {
        const body = this._openModal('反馈');
        const list = h('div', 'wannounce-box wfb-list');
        const footer = h('div', 'wf-footer');
        const prev = h('button', 'wf-pager-btn', '‹');
        const next = h('button', 'wf-pager-btn', '›');
        const info = h('span', 'wf-pager-info');
        let page = 1;
        let pages = 1;
        const sync = () => {
            info.textContent = `${page} / ${pages}`;
            prev.disabled = page <= 1;
            next.disabled = page >= pages;
        };
        prev.addEventListener('click', () => { if (page > 1) { page--; void render(); } });
        next.addEventListener('click', () => { if (page < pages) { page++; void render(); } });
        footer.append(prev, info, next);
        const render = async (): Promise<void> => {
            try {
                const data = await this.opts.api<FeedbackPage>(
                    `/admin/feedbacks?page=${page}&pageSize=6`);
                list.replaceChildren(...data.items.map((row) => {
                    const item = h('div', 'wfb-item');
                    const head = h('div', 'wfb-head');
                    head.append(
                        h('span', 'wfb-name', row.username),
                        h('span', 'wfb-time', formatDateTime(row.createdAt)));
                    item.append(head, h('p', 'wfb-content', row.content));
                    return item;
                }));
                if (!data.items.length)
                    list.replaceChildren(h('p', 'wf-empty', '暂无用户反馈。'));
                pages = Math.max(1, Math.ceil(data.total / data.pageSize));
                if (page > pages) { page = pages; void render(); return; }
            } catch (err) {
                list.replaceChildren(h('p', 'wf-empty', `反馈加载失败：${errorMessage(err)}`));
            }
            sync();
        };
        body.append(list, footer);
        sync();
        await render();
    }

    // ── 角色（个人中心）────────────────────────────────────────────────────

    private async openAvatar(): Promise<void> {
        let avatars: AvatarRow[];
        try { avatars = await this.opts.api<AvatarRow[]>('/hub/avatars'); }
        catch (err) { this.ui.showHint(`形象列表加载失败：${errorMessage(err)}`); return; }
        // 头部右上角（关闭按钮左侧）放「退出登录」：登出会话回到登录页。
        this._openFullscreen('角色', '🧑', (body, register) => {
            const tabs = h('div', 'wf-tabs');
            const content = h('div', 'wa-content');
            body.append(tabs, content);
            const defs: { id: string; label: string }[] = [
                { id: 'profile', label: '基础信息' },
                { id: 'model', label: '角色模型' },
                { id: 'security', label: '账号安全' }
            ];
            let active = 'profile';
            let stage: AvatarStage | null = null;
            const tabBtns = new Map<string, HTMLElement>();
            for (const d of defs) {
                const btn = h('button', 'wf-tab', d.label);
                btn.addEventListener('click', () => { active = d.id; syncTabs(); render(); });
                tabs.append(btn);
                tabBtns.set(d.id, btn);
            }
            const syncTabs = () => {
                for (const [id, btn] of tabBtns) btn.classList.toggle('active', id === active);
            };
            const render = () => {
                stage?.destroy();
                stage = null;
                // 离开角色模型页签（或初始渲染）：释放主应用的预览预热引用。
                this.opts.onPreviewAvatar?.(null);
                content.replaceChildren();
                if (active === 'profile') this._renderProfileTab(content);
                else if (active === 'security') this._renderSecurityTab(content);
                else this._renderModelTab(content, avatars, (s) => { stage = s; });
            };
            syncTabs();
            render();
            register(() => { stage?.destroy(); stage = null; this.opts.onPreviewAvatar?.(null); });
        }, this._buildLogoutButton());
    }

    /** 角色面板头部「退出登录」：POST /auth/logout 清除登录会话后整页跳回
     *  登录页（/），可重新登录。与主菜单头部的「关闭网页」退出不同：这里是
     *  标准登出——同时清掉 room-resume 重进标记，重新登录后走默认入口房间，
     *  不再原地恢复退出前的房间位置。接口失败（会话已失效等）也照常跳转。 */
    private _buildLogoutButton(): HTMLElement {
        const btn = h('button', 'wf-logout', '退出登录');
        btn.type = 'button';
        btn.addEventListener('click', async () => {
            btn.disabled = true;
            try { await this.opts.api('/auth/logout', 'POST'); } catch { /* 会话已失效也照常回登录页 */ }
            try { sessionStorage.removeItem(`room-resume:${this.opts.user.id}`); } catch { /* 隐私模式等 */ }
            location.assign('/');
        });
        return btn;
    }

    /** 页签一：基础信息（用户名 / 单位 → PATCH /me）。 */
    private _renderProfileTab(content: HTMLElement): void {
        const u = this.opts.user;
        const card = h('div', 'wa-card');
        card.append(h('p', 'wa-card-note', `用户编号 ${u.id} · ${levelTitle(u.level)} ${u.level}级`));
        const mk = (label: string, value: string, min: number, max: number) => {
            const row = h('label', 'wc-field');
            row.append(h('span', '', label));
            const input = h('input');
            input.type = 'text';
            input.value = value;
            input.minLength = min;
            input.maxLength = max;
            row.append(input);
            card.append(row);
            return input;
        };
        const name = mk('用户名（公开昵称）', u.username, 2, 20);
        const org = mk('单位', (u as unknown as { organization?: string }).organization ?? '', 2, 40);
        // 性别选择：两个互斥按钮，不选则默认男。
        let gender: 'male' | 'female' =
            (u as unknown as { gender?: string }).gender === 'female' ? 'female' : 'male';
        const genderField = h('div', 'wc-field');
        genderField.append(h('span', '', '性别'));
        const genderRow = h('div', 'wc-gender');
        const genderBtns = ([['male', '男'], ['female', '女']] as const).map(([key, label]) => {
            const b = h('button', 'wc-gender-btn', label);
            b.type = 'button';
            b.addEventListener('click', () => { gender = key; syncGender(); });
            genderRow.append(b);
            return { key, b };
        });
        const syncGender = () => genderBtns.forEach(({ key, b }) => b.classList.toggle('active', gender === key));
        syncGender();
        genderField.append(genderRow);
        name.closest('.wc-field')!.after(genderField);
        // 隐身模式：勾选后同房间其他玩家看不到自己（模型 + 名牌均隐藏）。
        // 仅管理身份（房主/主持人/平台管理员）可开启；其他角色置灰不可点，
        // 保存时强制 hidden=false——历史开启过隐身的普通用户保存资料即恢复
        // 正常可见，不会卡在隐身态无法关闭。moderator 为房间管理态异步查询
        // 结果，admin 角色兜底防查询未返回时误置灰。
        const canHide = this.moderator || this.opts.user.role === 'admin';
        const hiddenField = h('label', 'wc-field wc-check-field' + (canHide ? '' : ' wc-check-disabled'));
        const hiddenText = h('span', '', '隐身模式（别人看不到我）');
        const hidden = h('input');
        hidden.type = 'checkbox';
        hidden.disabled = !canHide;
        hidden.checked = canHide && (u as unknown as { hidden?: boolean }).hidden === true;
        const hiddenWrap = h('div', 'wc-check');
        hiddenWrap.append(hidden, h('span', '', canHide
            ? '开启后，其他玩家看不到我，自己的第三人称模型也会隐藏'
            : '仅房主 / 主持人 / 管理员可开启'));
        hiddenField.append(hiddenText, hiddenWrap);
        org.closest('.wc-field')!.after(hiddenField);
        const msg = h('p', 'wc-msg');
        const save = h('button', 'wc-confirm', '保存资料');
        save.addEventListener('click', async () => {
            save.disabled = true;
            msg.textContent = '正在保存…';
            try {
                const updated = await this.opts.api<Record<string, unknown>>('/me', 'PATCH', {
                    username: name.value.trim(),
                    organization: org.value.trim(),
                    gender,
                    // 无管理身份时无论勾选态一律上行 false（勾选框本就禁用置空）。
                    hidden: canHide && hidden.checked
                });
                Object.assign(this.opts.user, updated);
                this.refreshMenuIdentity();
                msg.textContent = '已保存。';
                this.ui.showHint('资料已更新');
            } catch (err) {
                msg.textContent = errorMessage(err);
            } finally {
                save.disabled = false;
            }
        });
        card.append(msg, save);
        content.append(card);
    }

    /** 页签三：账号安全（修改密码 → POST /me/password → 重新登录）。 */
    private _renderSecurityTab(content: HTMLElement): void {
        const card = h('div', 'wa-card');
        card.append(h('p', 'wa-card-note', '修改成功后需要重新登录。'));
        const mk = (label: string) => {
            const row = h('label', 'wc-field');
            row.append(h('span', '', label));
            const input = h('input');
            input.type = 'password';
            input.maxLength = 64;
            input.autocomplete = 'new-password';
            row.append(input);
            card.append(row);
            return input;
        };
        const oldPass = mk('当前密码');
        const newPass = mk('新密码（8–64 位）');
        newPass.autocomplete = 'new-password';
        const confirm = mk('确认新密码');
        const msg = h('p', 'wc-msg');
        const save = h('button', 'wc-confirm', '更新密码');
        save.addEventListener('click', async () => {
            if (newPass.value !== confirm.value) { msg.textContent = '两次输入的新密码不一致。'; return; }
            save.disabled = true;
            msg.textContent = '正在更新…';
            try {
                await this.opts.api('/me/password', 'POST', { oldPassword: oldPass.value, password: newPass.value });
                msg.textContent = '密码已更新，正在返回登录…';
                this.ui.showHint('密码已更新');
                window.setTimeout(() => location.assign('/'), 1200);
            } catch (err) {
                msg.textContent = errorMessage(err);
                save.disabled = false;
            }
        });
        card.append(msg, save);
        content.append(card);
    }

    /** 页签二：角色模型（三卡轮播：中间大卡 + 两侧小卡，左右切换）。
     *  性别已在「基础信息」中选定：本页签锁定为对应性别，仅展示该性别的形象，不可切换。
     *  中卡默认缩略图模式：模型同目录 thumb.png（继承 modelUrl 的 ?v= 版本参数，
     *  替换模型后缩略图同步失效），自适应显示区不裁切不变形；缺图回退首字圆块。
     *  两侧小卡始终展示前一个/后一个形象的缩略图+名字，点击直接跳选；
     *  仅一个形象时两侧自动隐藏（手机窄屏到桌面横屏同一布局，比例自适应）。
     *  3D 舞台（AvatarStage）保留为备用：点「3D 预览」懒创建并占中卡（两侧
     *  小卡不变），切回缩略图即销毁（释放独立 PlayCanvas 应用的 GPU 占用），
     *  两种模式「使用此角色」链路一致。
     *  setStage 把异步创建完成的 3D 舞台交还给页签容器统一销毁。 */
    private _renderModelTab(content: HTMLElement, avatars: AvatarRow[], setStage: (stage: AvatarStage | null) => void): void {
        if (!avatars.length) {
            content.append(h('p', 'wf-empty', '暂无可用的角色形象，请联系管理员上传。'));
            return;
        }
        const gender: Gender =
            (this.opts.user as unknown as { gender?: string }).gender === 'female' ? 'female' : 'male';
        let idx = 0;
        let stage: AvatarStage | null = null;
        let mode: 'thumb' | 'stage' = 'thumb';
        const wrap = h('div', 'wa-stage');
        // 三卡轮播：两侧小卡（可点击跳选）+ 中卡（缩略图 / 3D 舞台）。
        const carousel = h('div', 'wa-carousel');
        const sidePrev = h('button', 'wa-card-side');
        const sideNext = h('button', 'wa-card-side');
        for (const card of [sidePrev, sideNext]) {
            const box = h('div', 'wa-card-thumbbox');
            box.append(h('img', 'wa-card-thumb'), h('div', 'wa-card-fallback'));
            card.append(box, h('span', 'wa-card-name'));
            card.type = 'button';
        }
        const canvasBox = h('div', 'wa-canvas-box');
        const thumbBox = h('div', 'wa-thumb-box');
        const thumbImg = h('img', 'wa-thumb');
        const thumbFallback = h('div', 'wa-thumb-fallback');
        thumbBox.append(thumbImg, thumbFallback);
        canvasBox.append(thumbBox);
        carousel.append(sidePrev, canvasBox, sideNext);
        const note = h('div', 'wa-note', '正在加载…');
        const left = h('button', 'wa-arrow wa-arrow-left', '‹');
        const right = h('button', 'wa-arrow wa-arrow-right', '›');
        const hud = h('div', 'wa-hud');
        const use = h('button', 'wa-use', '使用此角色');
        const modeBtn = h('button', 'wa-mode', '3D 预览');
        // 形象名称并入按钮下方提示行前端（wa-hint-name:empty 时自动隐藏），
        // 按钮上方不再占一行。
        const hintName = h('span', 'wa-hint-name');
        const hintMode = h('span', '', '缩略图预览');
        const hint = h('div', 'wa-hint');
        hint.append(hintName, hintMode);
        hud.append(modeBtn, use, hint);
        wrap.append(carousel, note, left, right, hud);
        content.append(wrap);

        // 性别过滤：male/female 只看对应分类；neutral（磁盘顶层目录新增、
        // 未声明性别的形象）对男女用户都显示，保证运维往 avatars 目录里放
        // 新目录后任何用户都能立刻选用。config 标记 hidden 的形象两端全隐藏
        // （avatars.json 磁盘配置，如 B孙悟空 2026-10-14 先禁用）；手机端额外
        // 隐藏 mobileHidden 的形象（如 B 系 2026-10-09——仅 B邓丽君 不标记
        // 两端可选），桌面不受影响。
        const filtered = (): AvatarRow[] =>
            avatars.filter((a) => {
                if (a.config?.hidden === true) return false;
                if (isMobileRuntime() && a.config?.mobileHidden === true) return false;
                const g = a.config?.gender ?? 'neutral';
                return g === 'neutral' || g === gender;
            });
        // 打开面板即居中「使用中」的形象（此前固定从第 0 个开始，要翻找
        // 自己当前角色）；佩戴形象被隐藏/性别不符找不到时回落第 0 个。
        idx = Math.max(0, filtered().findIndex((a) => a.id === this.opts.user.avatarId));
        const syncHud = () => {
            const list = filtered();
            const cur = list[idx];
            hintName.textContent = cur ? `${cur.name}${cur.id === this.opts.user.avatarId ? '（使用中）' : ''}` : '';
            use.disabled = !cur || cur.id === this.opts.user.avatarId;
            left.disabled = list.length <= 1;
            right.disabled = list.length <= 1;
        };
        // 缩略图：与 player.glb 同目录的 thumb.png，继承 ?v= 版本参数；
        // 序号守卫丢弃迟到的旧图 onerror——不能用 src 字符串比对（img.src
        // 属性会被浏览器解析成绝对路径，与相对 URL 永不相等，404 回退失效）。
        let thumbSeq = 0;
        const showThumb = (av: AvatarRow): void => {
            const dir = av.modelUrl.slice(0, av.modelUrl.lastIndexOf('/') + 1);
            const q = av.modelUrl.includes('?') ? av.modelUrl.slice(av.modelUrl.indexOf('?')) : '';
            const seq = ++thumbSeq;
            thumbFallback.textContent = av.name.slice(0, 1) || av.id.slice(0, 1).toUpperCase();
            thumbImg.hidden = false;
            thumbFallback.hidden = true;
            thumbImg.onload = null;
            thumbImg.onerror = () => {
                if (seq !== thumbSeq) return;
                thumbImg.hidden = true;
                thumbFallback.hidden = false;
            };
            thumbImg.src = `${dir}thumb.png${q}`;
        };
        // 两侧小卡：前一个/后一个形象的缩略图+名字；环形列表取相邻项，
        // 仅一个形象时两侧隐藏。dataset.avatarId 供点击跳选定位。
        // 缩略图 404 回退同样用序号守卫（每张卡独立计数）。
        const sideSeqs = new WeakMap<HTMLElement, number>();
        const setSide = (card: HTMLElement, av: AvatarRow | null): void => {
            card.hidden = !av;
            if (!av) return;
            card.dataset.avatarId = av.id;
            const img = card.querySelector('img.wa-card-thumb') as HTMLImageElement | null;
            const fallback = card.querySelector('.wa-card-fallback') as HTMLElement | null;
            const name = card.querySelector('.wa-card-name') as HTMLElement | null;
            if (name) name.textContent = av.name;
            if (!img || !fallback) return;
            const dir = av.modelUrl.slice(0, av.modelUrl.lastIndexOf('/') + 1);
            const q = av.modelUrl.includes('?') ? av.modelUrl.slice(av.modelUrl.indexOf('?')) : '';
            const seq = (sideSeqs.get(card) ?? 0) + 1;
            sideSeqs.set(card, seq);
            fallback.textContent = av.name.slice(0, 1) || av.id.slice(0, 1).toUpperCase();
            img.hidden = false;
            fallback.hidden = true;
            img.onload = null;
            img.onerror = () => {
                if (sideSeqs.get(card) !== seq) return;
                img.hidden = true;
                fallback.hidden = false;
            };
            img.src = `${dir}thumb.png${q}`;
        };
        const renderSides = (): void => {
            const list = filtered();
            const n = list.length;
            setSide(sidePrev, n > 1 ? list[(idx - 1 + n) % n] : null);
            setSide(sideNext, n > 1 ? list[(idx + 1) % n] : null);
        };
        for (const card of [sidePrev, sideNext]) {
            card.addEventListener('click', () => {
                const list = filtered();
                const target = list.findIndex((a) => a.id === card.dataset.avatarId);
                if (target < 0 || target === idx) return;
                idx = target;
                void show();
            });
        }
        const show = async (): Promise<void> => {
            const list = filtered();
            if (!list.length) {
                hintName.textContent = '';
                note.textContent = '该分类下暂无形象';
                note.classList.remove('hidden');
                renderSides();
                return;
            }
            syncHud();
            renderSides();
            if (mode === 'thumb') {
                showThumb(list[idx]);
                note.classList.add('hidden');
            } else {
                note.textContent = '正在加载模型…';
                note.classList.remove('hidden');
                if (!stage) {
                    stage = await AvatarStage.create(canvasBox);
                    if (!canvasBox.isConnected) { stage.destroy(); stage = null; return; }
                    setStage(stage);
                }
                await stage.show(list[idx]);
                note.classList.add('hidden');
            }
            // 两模式都预热主应用资产：字节进 HTTP 缓存后点「使用」时
            // AvatarController.acquireAvatar 命中已解析缓存秒切。
            this.opts.onPreviewAvatar?.(list[idx]?.modelUrl ?? null);
        };
        const applyMode = () => {
            canvasBox.classList.toggle('thumb-mode', mode === 'thumb');
            modeBtn.textContent = mode === 'thumb' ? '3D 预览' : '返回缩略图';
            hintMode.textContent = mode === 'thumb' ? '缩略图预览' : '拖拽旋转 · 滚轮 / 双指缩放';
        };
        modeBtn.addEventListener('click', () => {
            if (mode === 'thumb') {
                mode = 'stage';
            } else {
                // 切回缩略图：销毁 3D 舞台释放独立应用的 GPU 占用；引擎
                // destroy 不摘 canvas 节点，残留的死画布会盖住缩略图，一并移除。
                mode = 'thumb';
                stage?.destroy();
                stage = null;
                setStage(null);
                canvasBox.querySelectorAll('canvas.wa-canvas').forEach((c) => c.remove());
            }
            applyMode();
            void show();
        });
        applyMode();
        left.addEventListener('click', () => {
            const list = filtered();
            idx = (idx - 1 + list.length) % list.length;
            void show();
        });
        right.addEventListener('click', () => {
            const list = filtered();
            idx = (idx + 1) % list.length;
            void show();
        });
        use.addEventListener('click', async () => {
            const cur = filtered()[idx];
            if (!cur || use.disabled) return;
            use.disabled = true;
            try {
                const updated = await this.opts.api<Record<string, unknown>>('/me', 'PATCH', { avatarId: cur.id });
                Object.assign(this.opts.user, updated);
                this.ui.showHint('形象已切换');
                // 本地第三人称模型立即重建；同房间的其他人经 player:profile 广播同步。
                this.opts.onAvatarChange?.(cur.id);
            } catch (err) {
                this.ui.showHint(`保存失败：${errorMessage(err)}`);
            } finally {
                syncHud();
                use.disabled = false;
            }
        });
        void show();
    }
}

/**
 * AvatarStage: a lightweight standalone PlayCanvas app that shows one avatar
 * GLB centered, with auto-playing looping animation, pointer-drag rotation
 * and wheel/pinch zoom (touch + mouse).
 */
class AvatarStage {
    private pc: typeof import('playcanvas') | null = null;
    private app: any = null;
    private camera: any = null;
    private holder: any = null;
    private observer: ResizeObserver | null = null;
    private destroyed = false;
    private target = { x: 0, y: 1, z: 0 };
    private distance = 3.2;
    private baseDistance = 3.2;
    private yaw = 0;
    private pitch = 0.16;
    private pointers = new Map<number, { x: number; y: number }>();
    private cache = new Map<string, any>();
    /** show() 过期序号：快速连续切换预览时丢弃迟到的旧加载结果。 */
    private _showSeq = 0;

    static async create(container: HTMLElement): Promise<AvatarStage> {
        const stage = new AvatarStage();
        const pc = await import('playcanvas');
        if (stage.destroyed || !container.isConnected) return stage;
        stage.pc = pc;
        const canvas = document.createElement('canvas');
        canvas.className = 'wa-canvas';
        canvas.style.touchAction = 'none';
        container.appendChild(canvas);
        const app = new pc.Application(canvas, { graphicsDeviceOptions: { alpha: true, antialias: true } });
        app.setCanvasFillMode(pc.FILLMODE_NONE, 1, 1);
        app.setCanvasResolution(pc.RESOLUTION_AUTO);
        app.scene.ambientLight = new pc.Color(0.72, 0.76, 0.82);
        const camera = new pc.Entity('StageCamera');
        camera.addComponent('camera', { clearColor: new pc.Color(0, 0, 0, 0), nearClip: 0.1, farClip: 120, fov: 40 });
        app.root.addChild(camera);
        const light = new pc.Entity('StageLight');
        light.addComponent('light', { type: 'directional', intensity: 1.8 });
        light.setEulerAngles(45, 25, 0);
        app.root.addChild(light);
        const fill = () => {
            if (!stage.destroyed && container.isConnected) {
                app.resizeCanvas(Math.max(1, container.clientWidth), Math.max(1, container.clientHeight));
            }
        };
        const observer = new ResizeObserver(fill);
        observer.observe(container);
        stage.app = app;
        stage.camera = camera;
        stage.observer = observer;
        fill();
        app.on('update', () => stage._orbit());
        app.start();
        stage._bindControls(canvas);
        return stage;
    }

    private _bindControls(canvas: HTMLCanvasElement): void {
        // 指针坐标统一换算成「画面坐标」再取位移：手机竖屏的虚拟横屏（#app-root
        // rotate 90°）下 clientX/Y 是物理竖屏坐标，直接用会把画面横向拖拽喂给
        // pitch（模型上下翻滚）。toVirtualLandscapePoint 与游戏输入共用同一映射，
        // 换算后桌面/原生横屏/虚拟横屏三者的拖拽方向完全一致。
        const pt = (e: PointerEvent): { x: number; y: number } => {
            const p = toVirtualLandscapePoint(e);
            return { x: p.x, y: p.y };
        };
        canvas.addEventListener('pointerdown', (e) => {
            canvas.setPointerCapture(e.pointerId);
            this.pointers.set(e.pointerId, pt(e));
        });
        canvas.addEventListener('pointermove', (e) => {
            const prev = this.pointers.get(e.pointerId);
            if (!prev) return;
            const cur = pt(e);
            if (this.pointers.size === 1) {
                this.yaw -= (cur.x - prev.x) * 0.01;
                this.pitch = Math.max(-0.15, Math.min(1.1, this.pitch + (cur.y - prev.y) * 0.004));
            } else if (this.pointers.size === 2) {
                const ids = [...this.pointers.keys()];
                const other = this.pointers.get(ids[0] === e.pointerId ? ids[1] : ids[0]);
                if (other) {
                    const oldDist = Math.hypot(prev.x - other.x, prev.y - other.y);
                    const newDist = Math.hypot(cur.x - other.x, cur.y - other.y);
                    if (oldDist > 0) this._zoom(oldDist / newDist);
                }
            }
            this.pointers.set(e.pointerId, cur);
        });
        const release = (e: PointerEvent) => { this.pointers.delete(e.pointerId); };
        canvas.addEventListener('pointerup', release);
        canvas.addEventListener('pointercancel', release);
        canvas.addEventListener('wheel', (e) => {
            e.preventDefault();
            this._zoom(1 + e.deltaY * 0.0012);
        }, { passive: false });
    }

    private _zoom(factor: number): void {
        this.distance = Math.max(this.baseDistance * 0.35, Math.min(this.baseDistance * 2.6, this.distance * factor));
    }

    private _orbit(): void {
        if (!this.camera || !this.pc) return;
        const d = this.distance;
        const Vec3 = this.pc.Vec3;
        const t = this.target;
        this.camera.setPosition(t.x + Math.sin(this.yaw) * d, t.y + d * this.pitch, t.z + Math.cos(this.yaw) * d);
        this.camera.lookAt(new Vec3(t.x, t.y, t.z));
    }

    /** Load + display an avatar; auto-plays its looping idle animation. */
    async show(avatar: AvatarRow): Promise<void> {
        if (!this.app || !this.pc || this.destroyed) return;
        // 过期守卫：上一个模型未加载完就切换时，旧的 show 仍在 await——迟到
        // 返回后若不丢弃，会把旧模型叠加到新模型上（两个模型同屏）。每次
        // show 递增序号，await 之后序号不一致即说明已被更新的切换取代。
        const seq = ++this._showSeq;
        if (this.holder) { this.holder.destroy(); this.holder = null; }
        const cfg = avatar.config ?? {};
        const asset = await this._load(avatar.modelUrl);
        if (this.destroyed || seq !== this._showSeq) return;
        const model = asset.resource.instantiateRenderEntity();
        const holder = new this.pc.Entity('AvatarHolder');
        holder.addChild(model);
        this.app.root.addChild(holder);
        const scale = (Number(cfg.avatarScale) || 1) * AVATAR_SIZE_SCALE;
        holder.setLocalScale(scale, scale, scale);
        holder.setLocalEulerAngles(0, Number(cfg.avatarRotationOffset) || 0, 0);
        const bounds = new this.pc.BoundingBox();
        let has = false;
        model.findComponents('render').forEach((r: any) => r.meshInstances.forEach((m: any) => {
            if (has) bounds.add(m.aabb);
            else { bounds.copy(m.aabb); has = true; }
        }));
        const t = has ? bounds.center.clone() : new this.pc.Vec3(0, 1, 0);
        this.target = { x: t.x, y: t.y, z: t.z };
        this.baseDistance = Math.max(2, has ? bounds.halfExtents.length() * 3.2 : 3);
        this.distance = this.baseDistance;
        this.yaw = 0;
        this.pitch = 0.16;
        const clips = asset.resource.animations || [];
        if (clips.length) {
            model.addComponent('anim', { activate: true });
            const layer = model.anim.baseLayer ?? model.anim.addLayer('Base');
            const states: [string, string][] = [];
            clips.forEach((clip: any, i: number) => {
                if (clip.resource) {
                    layer.assignAnimation(`clip${i}`, clip.resource, 1, true);
                    states.push([`clip${i}`, clip.resource.name || clip.name || `动画${i + 1}`]);
                }
            });
            const idle = states.find(([, n]) => n === (cfg.idleAnim ?? 'Idle')) ?? states[0];
            if (idle) layer.play(idle[0]);
        }
        this.holder = holder;
    }

    private async _load(url: string): Promise<any> {
        if (!this.cache.has(url)) {
            // meshopt/量化压缩模型换成浏览器内解码后的 Blob URL（网络只传
            // 压缩字节）；缓存 key 仍用原始 URL 与预热链路对齐。
            const finalUrl = await ensurePlayableGlb(url);
            if (this.destroyed || !this.app) throw new Error('预览已关闭');
            const asset = await new Promise<any>((resolve, reject) => {
                this.app.assets.loadFromUrlAndFilename(finalUrl, 'avatar.glb', 'container',
                    (err: unknown, a: any) => err ? reject(new Error('模型加载失败，请检查资源')) : resolve(a));
            });
            this.cache.set(url, asset);
        }
        // 预览资产只保留当前形象：换预览时上一形象的实体已在 show 开头销毁，
        // 这里把其解析数据（大 GLB 解析后可达百 MB 级）一并卸载——连续预览
        // 多个形象不再线性累积内存；切回旧形象经 HTTP/Blob 缓存快速重载。
        for (const [key, asset] of this.cache) {
            if (key === url) continue;
            this.cache.delete(key);
            try { this.app.assets.unload(asset); this.app.assets.remove(asset); } catch { /* 卸载失败不挡预览 */ }
        }
        return this.cache.get(url);
    }

    destroy(): void {
        this.destroyed = true;
        this.observer?.disconnect();
        this.observer = null;
        try { this.app?.destroy(); } catch { /* ignore */ }
        this.app = null;
        this.camera = null;
        this.holder = null;
        this.cache.clear();
    }
}

export { WorldUI };
